import { useEffect } from "react"
import {
	Stack,
	Heading,
	Table,
	Text,
	Inline,
	Money,
	Box,
	Button,
	TableDataCell,
	TableFooterDataCell,
	TableHeaderDataCell,
	Component,
	Icons,
	useDidMount,
	Grid,
	Col,
	useBreakpoints,
	Alert,
} from "@sembark-travel/ui/base"
import {
	GetFieldValue,
	SelectInputField,
	TextInputField,
	SwitchInputField,
	useFieldValue,
	useForm,
	isTruthy,
	useCurrencyPairRatioFieldValue,
	InlineSelectInputField,
} from "@sembark-travel/ui/form"
import {
	addMoney,
	getPercentageOfMoney,
	roundMoneyToPoint,
	TMoney,
	createMoney,
	moneyParseByDecimal,
	convertMoneyToCurrency,
	formatMoneyByDecimal,
	subtractMoney,
	multipleMoneyBy,
	getMoneyRatio,
	allocateMoneyTo,
	roundMoney,
} from "@sembark-travel/money"
import {
	PerPersonInclusionConfiguration,
	PerPersonAllocationNotPossibleError,
	PerPersonAllocationWarnings,
	PerPersonAllocationPriceMismatchError,
	PerPersonCostPriceSegregationTable,
	INewQuoteState,
	TPerPersonAllocationResult,
} from "./NewQuote"
import { PercentageInputField } from "@sembark-travel/ui/form"
import { InlineSelectTaxTypeInputField } from "../TaxTypes"
import { isDeepEqual } from "remeda"
import {
	normalizeNumber,
	numberToLocalString,
} from "@sembark-travel/number-utils"
import { useMemo } from "react"
import {
	IQuoteComponentsCostPricesDistributed,
	ITripQuote,
	TComponentKey,
	TPerPersonMarkupAllocation,
	TTaxAppliedOn,
	TPricePerTravelComponentAllocation,
	TPricePerPersonAllocation,
} from "./store"

function useComponentMarkupData(
	optionIndex: number,
	componentKey: TComponentKey
) {
	const name = `options[${optionIndex}].margins.per_component_allocation.${componentKey}`

	const { value: markup, onChange: changeMarkup } = useFieldValue<
		number | undefined | ""
	>(`${name}`)
	const { value: markupPercentage, onChange: changeMarkupPercentage } =
		useFieldValue<number | undefined | "">(`${name}_percentage`)

	return useMemo(() => {
		return {
			markup,
			markupPercentage,
			changeMarkup,
			changeMarkupPercentage,
		}
	}, [markup, markupPercentage, changeMarkup, changeMarkupPercentage])
}

function useComponentTaxData(optionIndex: number, componentKey: TComponentKey) {
	const name = `options[${optionIndex}].taxes.per_component_allocation.${componentKey}`

	const { value: taxPercentage, onChange: changeTaxPercentage } = useFieldValue<
		number | undefined | ""
	>(`${name}_percentage`)

	const { value: taxAppliedOn, onChange: changeTaxAppliedOn } =
		useFieldValue<TTaxAppliedOn>(`${name}_applied_on`)

	return useMemo(() => {
		return {
			taxPercentage,
			taxAppliedOn,
			changeTaxPercentage,
			changeTaxAppliedOn,
		}
	}, [taxPercentage, taxAppliedOn, changeTaxPercentage, changeTaxAppliedOn])
}

function getTotalGivenPriceWithoutFlighPriceInGivenCurrency(
	flightsResults: ReturnType<typeof getFlightComponentPriceCalculationValues>,
	givenPrice: TMoney,
	isCostPriceCurrencySameAsGivenPriceCurrency: boolean
) {
	let flightPriceInGivenCurrency = flightsResults.rounded

	if (!isCostPriceCurrencySameAsGivenPriceCurrency) {
		flightPriceInGivenCurrency = flightsResults.roundedInGiven
	}

	const totalGivenPriceInGivenCurrencyWithoutFlightPrice = subtractMoney(
		givenPrice,
		flightPriceInGivenCurrency
	)

	return totalGivenPriceInGivenCurrencyWithoutFlightPrice
}

// eslint-disable-next-line react-refresh/only-export-components
export function getComponentPriceCalculationValues({
	base,
	markup,
	tax,
	taxIncluded,
	globalTaxAppliedOn,
	componentTaxPercentage,
	componentTaxAppliedOn = "cost_plus_markup",
	rounding,
	currency,
	givenCurrency,
	exchangeRate,
}: {
	base: TMoney
	markup: number
	tax?: number
	taxIncluded: boolean
	globalTaxAppliedOn?: TTaxAppliedOn
	componentTaxPercentage?: number
	componentTaxAppliedOn?: TTaxAppliedOn
	currency: string
	rounding?: number
	givenCurrency?: string
	exchangeRate?: number
}): IComponentCalculatedValues {
	const baseMoney = base

	if (baseMoney.amount === 0) {
		return {
			markup: createMoney(0, currency),
			tax: createMoney(0, currency),
			unrounded: createMoney(0, currency),
			rounded: createMoney(0, currency),
			unroundedInGiven: createMoney(0, givenCurrency || currency),
			roundedInGiven: createMoney(0, givenCurrency || currency),
		}
	}

	const markupMoney: TMoney = moneyParseByDecimal(markup, currency)
	// Add markup to base
	const subtotalMoney = addMoney(baseMoney, markupMoney)
	// Calculate tax
	let taxMoney: TMoney = createMoney(0, currency)
	const taxAppliedOn = globalTaxAppliedOn || componentTaxAppliedOn
	if (taxIncluded && taxAppliedOn) {
		if (tax) {
			if (taxAppliedOn === "cost_plus_markup") {
				if (subtotalMoney.amount > 0) {
					taxMoney = getPercentageOfMoney(subtotalMoney, tax)
				}
			} else if (taxAppliedOn === "only_markup") {
				if (markupMoney.amount > 0) {
					taxMoney = getPercentageOfMoney(markupMoney, tax)
				}
			}
		} else if (componentTaxPercentage) {
			if (taxAppliedOn === "cost_plus_markup") {
				if (subtotalMoney.amount > 0) {
					taxMoney = getPercentageOfMoney(subtotalMoney, componentTaxPercentage)
				}
			} else if (taxAppliedOn === "only_markup") {
				if (markupMoney.amount > 0) {
					taxMoney = getPercentageOfMoney(markupMoney, componentTaxPercentage)
				}
			}
		}
	}

	const markupPercentage =
		baseMoney.amount > 0 ? getMoneyRatio(markupMoney, baseMoney, 0) * 100 : 0

	// Unrounded total
	const unroundedMoney = addMoney(subtotalMoney, taxMoney)
	// Rounded total
	let roundedMoney = unroundedMoney
	if (rounding) {
		roundedMoney = roundMoneyToPoint(unroundedMoney, rounding)
	}

	let unroundedInGiven = unroundedMoney
	let roundedInGiven = roundedMoney

	if (givenCurrency && exchangeRate && givenCurrency !== currency) {
		unroundedInGiven = convertMoneyToCurrency(
			unroundedMoney,
			givenCurrency,
			exchangeRate
		)
		if (rounding) {
			roundedInGiven = roundMoneyToPoint(unroundedInGiven, rounding)
		} else {
			roundedInGiven = unroundedInGiven
		}
	}

	return {
		markup: markupMoney,
		markupPercentage: markupPercentage,
		tax: taxMoney,
		unrounded: unroundedMoney,
		rounded: roundedMoney,
		unroundedInGiven: unroundedInGiven,
		roundedInGiven: roundedInGiven,
	}
}

/**
 * DEV NOTE: Flight Component Price Calculation Utility
 *
 * This utility, `getFlightComponentPriceCalculationValues`, is specifically designed for handling
 * the calculation of markup and tax for the "flights" component in our pricing strategy.
 *
 * ## Why is this different from `getComponentPriceCalculationValues`?
 *
 * - For most components (hotels, cabs, activities, extras), the user provides a cost price and a markup,
 *   and the system calculates the final (given) price by adding markup and tax to the cost price.
 *   The utility `getComponentPriceCalculationValues` is built for this flow: it takes a base cost, a markup,
 *   and tax settings, and computes the final price.
 *
 * - Flights are different: In the quotation form, the user directly enters both the cost price and the
 *   final (given) price for flights. The markup and tax are not inputs, but rather derived values.
 *   The goal is to show the user, for the flights row, how much of the given price is markup and how much is tax,
 *   based on the user-inputted tax percentage and tax application mode.
 *   This is purely for informational/analytical purposes in the table and does not affect the final total
 *   given price calculation.
 **/

// eslint-disable-next-line react-refresh/only-export-components
export function getFlightComponentPriceCalculationValues({
	costPrice,
	givenPriceWithTax,
	taxPercentage,
	taxIncluded,
	taxAppliedOn = "cost_plus_markup",
	currency,
	givenCurrency,
	exchangeRate,
	rounding,
}: {
	costPrice: TMoney
	givenPriceWithTax: TMoney
	taxPercentage: number
	taxIncluded: boolean
	taxAppliedOn: TTaxAppliedOn
	currency: string
	givenCurrency?: string
	exchangeRate?: number
	rounding?: number
}) {
	if (costPrice.amount === 0) {
		return {
			markup: createMoney(0, currency),
			tax: createMoney(0, currency),
			unrounded: createMoney(0, currency),
			rounded: createMoney(0, currency),
			final: createMoney(0, currency),
			unroundedInGiven: createMoney(0, givenCurrency || currency),
			roundedInGiven: createMoney(0, givenCurrency || currency),
		}
	}

	let markupMoney = createMoney(0, currency)
	let taxMoney = createMoney(0, currency)

	const taxRate = Number(taxPercentage) / 100

	if (taxIncluded) {
		if (taxAppliedOn === "cost_plus_markup") {
			const costPlusMarkup = moneyParseByDecimal(
				Number(formatMoneyByDecimal(givenPriceWithTax)) / (1 + taxRate),
				currency
			)
			markupMoney = subtractMoney(costPlusMarkup, costPrice)

			const taxBase = addMoney(costPrice, markupMoney)

			if (taxBase.amount > 0) {
				taxMoney = getPercentageOfMoney(taxBase, taxPercentage)
			} else {
				taxMoney.amount = 0
				markupMoney = subtractMoney(givenPriceWithTax, costPrice)
			}
		} else if (taxAppliedOn === "only_markup") {
			markupMoney = moneyParseByDecimal(
				(Number(formatMoneyByDecimal(givenPriceWithTax)) -
					Number(formatMoneyByDecimal(costPrice))) /
					(1 + taxRate),
				currency
			)

			if (markupMoney.amount > 0) {
				taxMoney = getPercentageOfMoney(markupMoney, taxPercentage)
			} else {
				taxMoney.amount = 0
				markupMoney = subtractMoney(givenPriceWithTax, costPrice)
			}
		}
	} else {
		markupMoney = subtractMoney(givenPriceWithTax, costPrice)
	}

	// Unrounded total = givenPriceWithTax(addMoney(costPrice, markupMoney, taxMoney))
	const unroundedMoney = givenPriceWithTax

	const markupPercentage =
		costPrice.amount > 0
			? normalizeNumber(getMoneyRatio(markupMoney, costPrice, 0) * 100, 6)
			: 0

	// Rounded total
	let roundedMoney = unroundedMoney
	if (rounding) {
		roundedMoney = roundMoneyToPoint(unroundedMoney, rounding)
	}

	let unroundedInGiven = unroundedMoney
	let roundedInGiven = roundedMoney
	if (givenCurrency && exchangeRate && givenCurrency !== currency) {
		unroundedInGiven = convertMoneyToCurrency(
			unroundedMoney,
			givenCurrency,
			exchangeRate
		)
		if (rounding) {
			roundedInGiven = roundMoneyToPoint(unroundedInGiven, rounding)
		} else {
			roundedInGiven = unroundedInGiven
		}
	}

	return {
		markup: markupMoney,
		markupPercentage: markupPercentage,
		tax: taxMoney,
		taxPercentage: taxPercentage,
		taxAppliedOn: taxAppliedOn,
		unrounded: unroundedMoney,
		rounded: roundedMoney,
		unroundedInGiven: unroundedInGiven,
		roundedInGiven: roundedInGiven,
	}
}

function ComponentAbsoluteMarginInputField({
	margin,
	marginPercentage,
	marginName,
	onMarginPercentageChange: changeMarginPercentage,
	costPrice,
	showPercentage,
	disabled,
	readOnly = false,
}: {
	costPrice: TMoney
	showPercentage?: boolean
	margin: number | undefined | ""
	marginPercentage: number | undefined | ""
	marginName: string
	onMarginPercentageChange?: (value: number | undefined | "") => void
	disabled?: boolean
	readOnly?: boolean
}) {
	useEffect(() => {
		if (disabled) {
			return
		}
		const formattedCost = Number(formatMoneyByDecimal(costPrice) || 0)
		const currentMargin = Number(margin || 0)

		const newPercentage = formattedCost
			? normalizeNumber((currentMargin * 100) / formattedCost, 6)
			: 0

		const h = setTimeout(() => {
			if (newPercentage !== Number(marginPercentage || 0)) {
				changeMarginPercentage?.(newPercentage)
			}
		}, 100)

		return () => clearTimeout(h)
	}, [margin, costPrice, marginPercentage, disabled, changeMarginPercentage])

	if (readOnly) {
		return (
			<Inline justifyContent="center" alignItems="center">
				<Text>{numberToLocalString(Number(margin || 0), 2)}</Text>
			</Inline>
		)
	}

	return (
		<Inline justifyContent="center" alignItems="center">
			<TextInputField
				type="number"
				name={marginName}
				style={{ width: "100px" }}
				size="sm"
				min={0}
				help={
					showPercentage
						? `= ${normalizeNumber(Number(marginPercentage || 0), 2)}%`
						: null
				}
				disabled={disabled}
			/>
		</Inline>
	)
}

function ComponentPercentageMarginInputField({
	costPrice,
	margin,
	marginPercentage,
	marginPercentageName,
	onChange: changeMargin,
	disabled,
}: {
	costPrice: TMoney
	margin: number | undefined | ""
	marginPercentage: number | undefined | ""
	marginPercentageName: string
	onChange?: (value: number | undefined | "") => void
	disabled?: boolean
}) {
	useEffect(() => {
		if (disabled) {
			return
		}
		const formattedCost = Number(formatMoneyByDecimal(costPrice) || 0)
		const newMargin = normalizeNumber(
			(Number(marginPercentage || 0) * formattedCost) / 100,
			4
		)

		const h = setTimeout(() => {
			if (newMargin !== Number(margin || 0)) {
				changeMargin?.(newMargin)
			}
		}, 100)

		return () => clearTimeout(h)
	}, [marginPercentage, costPrice, margin, changeMargin, disabled])

	return (
		<Inline justifyContent="center" alignItems="center">
			<PercentageInputField
				name={marginPercentageName}
				help={`= ${numberToLocalString(Number(margin || 0), 0)}`}
				size="sm"
				disabled={disabled}
			/>
		</Inline>
	)
}

function ComponentMarginInputField({
	costPrice,
	markupUsePercentage,
	margin,
	marginPercentage,
	marginName,
	marginPercentageName,
	onMarginChange: changeMargin,
	onMarginPercentageChange: changeMarginPercentage,
	disabled,
	readOnly = false,
}: {
	costPrice: TMoney
	markupUsePercentage: boolean
	margin: number | undefined | ""
	marginPercentage: number | undefined | ""
	marginName: string
	marginPercentageName: string
	onMarginChange?: (value: number | undefined | "") => void
	onMarginPercentageChange?: (value: number | undefined | "") => void
	disabled?: boolean
	readOnly?: boolean
}) {
	if (markupUsePercentage) {
		return (
			<ComponentPercentageMarginInputField
				costPrice={costPrice}
				margin={margin}
				marginPercentage={marginPercentage}
				marginPercentageName={marginPercentageName}
				onChange={changeMargin}
				disabled={disabled}
			/>
		)
	}
	return (
		<ComponentAbsoluteMarginInputField
			costPrice={costPrice}
			margin={margin}
			marginPercentage={marginPercentage}
			marginName={marginName}
			onMarginPercentageChange={changeMarginPercentage}
			showPercentage
			disabled={disabled}
			readOnly={readOnly}
		/>
	)
}

function ComponentTaxCell({ componentTax }: { componentTax: TMoney }) {
	return <Money money={componentTax} />
}

function ComponentUnroundedTotalCell({
	componentUnroundedAmount,
}: {
	componentUnroundedAmount: TMoney
}) {
	return <Money money={componentUnroundedAmount} fontWeight="semibold" />
}

function ComponentRoundedTotalCell({
	componentRoundedAmount,
}: {
	componentRoundedAmount: TMoney
}) {
	return (
		<Money money={componentRoundedAmount} fontWeight="semibold" showCurrency />
	)
}

function ComponentTaxAppliedOnInputField({
	optionIndex,
	componentKey,
	globalTaxAppliedOn,
	taxEnabled,
	disabled,
}: {
	optionIndex: number
	componentKey: TComponentKey
	taxEnabled?: boolean
	globalTaxAppliedOn?: string
	disabled?: boolean
}) {
	if (!taxEnabled) {
		return <Text textAlign="center">-</Text>
	}

	if (globalTaxAppliedOn) {
		return <Text>{globalTaxAppliedOn}</Text>
	}
	return (
		<SelectInputField
			name={`options[${optionIndex}].taxes.per_component_allocation.${componentKey}_applied_on`}
			size="sm"
			disabled={disabled}
		>
			<option value="cost_plus_markup">Cost + Markup</option>
			<option value="only_markup">Only Markup</option>
		</SelectInputField>
	)
}

function ComponentTaxPercentageInputField({
	optionIndex,
	componentKey,
	taxEditablePerComponent,
	globalTaxPercentage,
	componentTaxPercentage,
	changeEditingTotal,
	changeComponentTaxPercentage,
	globalTaxEnabled,
	disabled,
}: {
	optionIndex: number
	componentKey: TComponentKey
	taxEditablePerComponent: boolean
	globalTaxPercentage?: number
	componentTaxPercentage?: number
	changeEditingTotal?: (value: 0 | 1) => void
	changeComponentTaxPercentage: (value: number | undefined | "") => void
	globalTaxEnabled: boolean
	disabled?: boolean
}) {
	useDidMount(() => {
		const h = setTimeout(() => {
			if (!componentTaxPercentage) {
				changeComponentTaxPercentage(Number(globalTaxPercentage || 0))
			}
		}, 100)

		return () => clearTimeout(h)
	})

	if (!globalTaxEnabled) {
		return <Text textAlign={"center"}>-</Text>
	}

	if (!taxEditablePerComponent) {
		return (
			<Inline gap="1" alignItems="center" justifyContent="center">
				<Text>{globalTaxPercentage || 0}%</Text>
				{!disabled ? (
					<Button
						size="sm"
						level="tertiary"
						onClick={() => {
							changeEditingTotal?.(0)
						}}
					>
						<Icons.Pencil />
					</Button>
				) : null}
			</Inline>
		)
	}

	return (
		<Inline gap="1" alignItems="center" justifyContent="center">
			<PercentageInputField
				name={`options[${optionIndex}].taxes.per_component_allocation.${componentKey}_percentage`}
				size="sm"
				disabled={disabled}
			/>
		</Inline>
	)
}

interface IComponentCalculatedValues {
	markup: TMoney
	markupPercentage?: number
	tax: TMoney
	rounded: TMoney
	unrounded: TMoney
	unroundedInGiven: TMoney
	roundedInGiven: TMoney
}

function ComponentMarkupTaxPricingSummaryRow({
	label,
	totalCostPrice,
	summary,
	costPriceCurrency,
	givenPriceCurrency,
}: {
	label: string
	givenPriceCurrency: string
	totalCostPrice: TMoney
	costPriceCurrency: string
	summary: {
		totalUnrounded: TMoney
		totalTax: TMoney
		totalRounded: TMoney
		totalMarkup: TMoney
		totalUnroundedInGiven: TMoney
		totalRoundedInGiven: TMoney
	}
}) {
	return (
		<tr
			style={{
				fontStyle: label === "Sub-Total" ? "italic" : undefined,
			}}
		>
			<TableFooterDataCell
				bgColor={label === "Sub-Total" ? "inset" : "warning"}
				fontWeight="semibold"
			>
				{label}
			</TableFooterDataCell>
			<TableFooterDataCell
				bgColor={label === "Sub-Total" ? "inset" : "warning"}
				textAlign="right"
				fontWeight="semibold"
			>
				<Money money={totalCostPrice} />
			</TableFooterDataCell>
			<TableFooterDataCell
				bgColor={label === "Sub-Total" ? "inset" : "warning"}
				textAlign="center"
				fontWeight="semibold"
			>
				<Money money={summary.totalMarkup} />
			</TableFooterDataCell>
			<TableFooterDataCell
				bgColor={label === "Sub-Total" ? "inset" : "warning"}
				fontWeight="semibold"
			></TableFooterDataCell>
			<TableFooterDataCell
				bgColor={label === "Sub-Total" ? "inset" : "warning"}
				fontWeight="semibold"
			></TableFooterDataCell>
			<TableFooterDataCell
				bgColor={label === "Sub-Total" ? "inset" : "warning"}
				textAlign="right"
				fontWeight="semibold"
			>
				<Money money={summary.totalTax} />
			</TableFooterDataCell>
			<TableFooterDataCell
				bgColor={label === "Sub-Total" ? "inset" : "warning"}
				textAlign="right"
				fontWeight="semibold"
			>
				<Money money={summary.totalUnrounded} />
			</TableFooterDataCell>

			{givenPriceCurrency !== costPriceCurrency ? (
				<TableFooterDataCell
					bgColor={label === "Sub-Total" ? "inset" : "warning"}
					textAlign="right"
					fontWeight="semibold"
				>
					<Money money={summary.totalUnroundedInGiven} />
				</TableFooterDataCell>
			) : null}

			<TableFooterDataCell
				bgColor={label === "Sub-Total" ? "inset" : "warning"}
				textAlign="right"
				fontWeight="semibold"
			>
				<Money money={summary.totalRoundedInGiven} showCurrency />
			</TableFooterDataCell>
		</tr>
	)
}

function ComponentMarkupTableRow({
	optionIndex,
	base,
	markupUsePercentage = false,
	componentKey,
	componentLabel,
	globalTaxEnabled,
	globalTaxPercentage,
	costPriceCurrency,
	givenPriceCurrency,
	globalTaxAppliedOn,
	taxEditablePerComponent = true,
	values,
	componentTaxPercentage,
	componentMarkup,
	componentMarkupPercentage,
	changeMarkup,
	changeMarkupPercentage,
	changeComponentTaxPercentage,
	changeEditingTotal,
	disableMarkupEditing,
	disableTaxEditing,
	readOnlyMarkup,
}: {
	optionIndex: number
	base: TMoney
	markupUsePercentage?: boolean
	componentKey: TComponentKey
	componentLabel: string
	globalTaxEnabled: boolean
	globalTaxPercentage?: number
	costPriceCurrency: string
	givenPriceCurrency?: string
	globalTaxAppliedOn?: TTaxAppliedOn
	taxEditablePerComponent?: boolean
	values: IComponentCalculatedValues
	componentTaxPercentage?: number
	componentMarkup?: number
	componentMarkupPercentage?: number
	changeMarkup?: (value: number | undefined | "") => void
	changeMarkupPercentage?: (value: number | undefined | "") => void
	changeComponentTaxPercentage: (value: number | undefined | "") => void
	changeEditingTotal?: (value: 0 | 1) => void
	disableMarkupEditing?: boolean
	disableTaxEditing?: boolean
	readOnlyMarkup?: boolean
}) {
	if (base.amount === 0) {
		return null
	}

	return (
		<tr>
			<TableDataCell bgColor="inset">{componentLabel}</TableDataCell>
			<TableDataCell textAlign="right">
				<Money money={base} />
			</TableDataCell>
			<TableDataCell textAlign="center">
				<ComponentMarginInputField
					costPrice={base}
					markupUsePercentage={markupUsePercentage}
					margin={componentMarkup}
					marginPercentage={componentMarkupPercentage}
					marginName={`options[${optionIndex}].margins.per_component_allocation.${componentKey}`}
					marginPercentageName={`options[${optionIndex}].margins.per_component_allocation.${componentKey}_percentage`}
					onMarginChange={changeMarkup}
					onMarginPercentageChange={changeMarkupPercentage}
					disabled={disableMarkupEditing}
					readOnly={readOnlyMarkup}
				/>
			</TableDataCell>
			<TableDataCell textAlign="center" style={{ width: "180px" }}>
				<ComponentTaxAppliedOnInputField
					optionIndex={0}
					componentKey={componentKey}
					globalTaxAppliedOn={
						globalTaxAppliedOn
							? globalTaxAppliedOn === "cost_plus_markup"
								? "Cost + Markup"
								: "Only Markup"
							: undefined
					}
					taxEnabled={globalTaxEnabled}
					disabled={disableTaxEditing}
				/>
			</TableDataCell>
			<TableDataCell textAlign="center" style={{ width: "180px" }}>
				<ComponentTaxPercentageInputField
					optionIndex={0}
					componentKey={componentKey}
					taxEditablePerComponent={taxEditablePerComponent}
					globalTaxPercentage={globalTaxPercentage}
					componentTaxPercentage={componentTaxPercentage}
					changeEditingTotal={changeEditingTotal}
					changeComponentTaxPercentage={changeComponentTaxPercentage}
					globalTaxEnabled={globalTaxEnabled}
					disabled={disableTaxEditing}
				/>
			</TableDataCell>
			<TableDataCell textAlign="right">
				<ComponentTaxCell componentTax={values.tax} />
			</TableDataCell>
			<TableDataCell textAlign="right">
				<ComponentUnroundedTotalCell
					componentUnroundedAmount={values.unrounded}
				/>
			</TableDataCell>
			{givenPriceCurrency !== costPriceCurrency ? (
				<TableDataCell textAlign="right">
					<ComponentUnroundedTotalCell
						componentUnroundedAmount={values.unroundedInGiven}
					/>
				</TableDataCell>
			) : null}
			<TableDataCell textAlign="right">
				<ComponentRoundedTotalCell
					componentRoundedAmount={values.roundedInGiven}
				/>
			</TableDataCell>
		</tr>
	)
}

const ROUNDING_OPTIONS = [undefined, 1, 5, 10, 50, 100]

export function ComponentMarkupTaxPricingBifurcation({
	optionIndex,
	quoteComponentPrices,
	isPerPersonPricingEnabled = false,
	perPersonAllocationNotPossible,
	...props
}: {
	optionIndex: number
	quoteComponentPrices: IQuoteComponentsCostPricesDistributed
	isPerPersonPricingEnabled?: boolean
	perPersonAllocationNotPossible?: boolean
	readOnly?: boolean
}) {
	const { value: costPriceCurrency } = useFieldValue<string>("costing_currency")
	const { value: givenPriceCurrency } = useFieldValue<string>("given_currency")
	const { value: givenPrice } = useFieldValue<number | undefined>(
		`options[${optionIndex}].given_price`
	)
	const { value: option1GivenPricesPerComponent } = useFieldValue<
		Array<TPricePerTravelComponentAllocation> | undefined
	>("options[0].given_prices_per_component")

	const { xl } = useBreakpoints()

	const { readOnly: readOnlyMode = false } = props

	const GIVEN_PRICE_CURRENCY = givenPriceCurrency || "INR"

	const { value: costPriceGivenPriceCurrencyPairRatio } =
		useCurrencyPairRatioFieldValue(
			"currency_pairs_obj",
			costPriceCurrency,
			GIVEN_PRICE_CURRENCY
		)

	const exchangeRate = costPriceGivenPriceCurrencyPairRatio
		? costPriceGivenPriceCurrencyPairRatio
		: 1

	const isCostPriceCurrencySameAsGivenPriceCurrency =
		costPriceCurrency === GIVEN_PRICE_CURRENCY

	// @dev-note- The reason we have currently used optionIndex=0 for global settings like (globalMarkup, globalTaxPercentage)
	// is because we want the control for global settings for pricing should be controlled from option 1

	const {
		value: markup_use_percentage,
		onChange: changeGlobalMarkupUsePercentage,
	} = useFieldValue<0 | 1>(`options[0].margins.use_percentage`)

	const {
		value: global_tax_editing_total,
		onChange: changeGlobalTaxEditingTotal,
	} = useFieldValue<0 | 1>(`options[0].taxes.editing_total`)

	const { value: global_tax_enabled } = useFieldValue<boolean>(
		`options[0].gst_included`
	)
	const { value: global_tax_percentage } = useFieldValue<number | undefined>(
		`options[0].taxes.total_percentage`
	)
	const { value: global_rounding } = useFieldValue<number | undefined>(
		`options[0].margins.rounding_point`
	)

	const { value: _total_applied_on, onChange: changeTotalAppliedOn } =
		useFieldValue<TTaxAppliedOn | "">(`options[0].taxes.total_applied_on`)

	const {
		totalPrice: totalPriceInCostingCurrency, //total cost price with flight price(flight_given_price) in costing currency,
		flightPrice: flightGivenPriceInCostingCurrency, //flight_given_price(given price of flight) in costing currency,
		flightCostPrice: flightCostPriceInCostingCurrency, //flight_calculated_price(cost price of flight) in costing currency,
		totalHotelPrice: totalHotelPriceInCostingCurrency,
		totalCabsOnlyPrice: totalCabsOnlyPriceInCostingCurrency,
		totalTravelActivitiesOnlyPrice:
			totalTravelActivitiesOnlyPriceInCostingCurrency,
		totalExtrasPrice: totalExtrasPriceInCostingCurrency,
	} = quoteComponentPrices

	const totalCostPriceWithoutFlightPrice = useMemo(() => {
		return Number(
			formatMoneyByDecimal(
				subtractMoney(
					totalPriceInCostingCurrency,
					flightGivenPriceInCostingCurrency
				)
			)
		)
	}, [totalPriceInCostingCurrency, flightGivenPriceInCostingCurrency])

	const totalCostPriceWithFlightPrice = useMemo(() => {
		return Number(
			formatMoneyByDecimal(
				addMoney(
					moneyParseByDecimal(
						totalCostPriceWithoutFlightPrice,
						costPriceCurrency
					),
					flightCostPriceInCostingCurrency
				)
			)
		)
	}, [
		costPriceCurrency,
		flightCostPriceInCostingCurrency,
		totalCostPriceWithoutFlightPrice,
	])

	const globalTaxEnabled = isTruthy(global_tax_enabled)
	const globalTaxPercentage = global_tax_percentage
	const markupUsePercentage = isTruthy(markup_use_percentage)
	const taxEditablePerComponent = !isTruthy(global_tax_editing_total)
	const globalRounding = global_rounding
	const globalTaxAppliedOn = _total_applied_on || undefined
	const disableMarkupEditingForOption = optionIndex !== 0 || readOnlyMode
	const disableTaxEditingForOption = optionIndex !== 0 || readOnlyMode
	const disableRoundingEditingForOption = optionIndex !== 0 || readOnlyMode
	const fixedLayoutTable = xl ? true : false

	// @dev-note- The reason we have used optionIndex=0 for per component markup and tax data is
	// because we want the control for component markup and tax data per component
	// should be handled based on the option1,
	// we are currenly not allowing to add different markup and tax per component separately for each option
	// expect for hotels as currently quotating page only supports options mainly for hotels

	const {
		markup: hotelMarkup,
		markupPercentage: hotelMarkupPercentage,
		changeMarkup: changeHotelMarkup,
		changeMarkupPercentage: changeHotelMarkupPercentage,
	} = useComponentMarkupData(optionIndex, "hotels")
	const {
		taxPercentage: hotelTaxPercentage,
		taxAppliedOn: hotelTaxAppliedOn,
		changeTaxPercentage: changeHotelTaxPercentage,
	} = useComponentTaxData(0, "hotels")

	const {
		markup: cabsMarkup,
		markupPercentage: cabsMarkupPercentage,
		changeMarkup: changeCabsMarkup,
		changeMarkupPercentage: changeCabsMarkupPercentage,
	} = useComponentMarkupData(0, "cabs")
	const {
		taxPercentage: cabsTaxPercentage,
		taxAppliedOn: cabsTaxAppliedOn,
		changeTaxPercentage: changeCabsTaxPercentage,
	} = useComponentTaxData(0, "cabs")

	const {
		markup: activitiesMarkup,
		markupPercentage: activitiesMarkupPercentage,
		changeMarkup: changeActivitiesMarkup,
		changeMarkupPercentage: changeActivitiesMarkupPercentage,
	} = useComponentMarkupData(0, "activities")
	const {
		taxPercentage: activitiesTaxPercentage,
		taxAppliedOn: activitiesTaxAppliedOn,
		changeTaxPercentage: changeActivitiesTaxPercentage,
	} = useComponentTaxData(0, "activities")

	const {
		markup: extrasMarkup,
		markupPercentage: extrasMarkupPercentage,
		changeMarkup: changeExtrasMarkup,
		changeMarkupPercentage: changeExtrasMarkupPercentage,
	} = useComponentMarkupData(0, "extras")
	const {
		taxPercentage: extrasTaxPercentage,
		taxAppliedOn: extrasTaxAppliedOn,
		changeTaxPercentage: changeExtrasTaxPercentage,
	} = useComponentTaxData(0, "extras")

	const {
		taxPercentage: flightsTaxPercentage,
		taxAppliedOn: flightsTaxAppliedOn,
		changeTaxPercentage: changeFlightsTaxPercentage,
	} = useComponentTaxData(0, "flights")

	const hotelResults = useMemo(
		() =>
			getComponentPriceCalculationValues({
				base: totalHotelPriceInCostingCurrency,
				markup: Number(hotelMarkup || 0),
				tax:
					globalTaxEnabled && !taxEditablePerComponent
						? Number(globalTaxPercentage || 0)
						: 0,
				taxIncluded: globalTaxEnabled,
				currency: costPriceCurrency,
				globalTaxAppliedOn: globalTaxAppliedOn || undefined,
				componentTaxPercentage:
					taxEditablePerComponent && globalTaxEnabled
						? Number(hotelTaxPercentage || 0)
						: undefined,
				componentTaxAppliedOn: hotelTaxAppliedOn,
				rounding: globalRounding,
				givenCurrency: GIVEN_PRICE_CURRENCY,
				exchangeRate: exchangeRate,
			}),
		[
			totalHotelPriceInCostingCurrency,
			hotelMarkup,
			globalTaxEnabled,
			globalTaxPercentage,
			taxEditablePerComponent,
			hotelTaxPercentage,
			hotelTaxAppliedOn,
			globalTaxAppliedOn,
			costPriceCurrency,
			globalRounding,
			GIVEN_PRICE_CURRENCY,
			exchangeRate,
		]
	)

	const cabsResults = useMemo(() => {
		return getComponentPriceCalculationValues({
			base: totalCabsOnlyPriceInCostingCurrency,
			markup: Number(cabsMarkup || 0),
			tax:
				globalTaxEnabled && !taxEditablePerComponent
					? Number(globalTaxPercentage || 0)
					: 0,
			taxIncluded: globalTaxEnabled,
			currency: costPriceCurrency,
			globalTaxAppliedOn: globalTaxAppliedOn || undefined,
			componentTaxPercentage:
				taxEditablePerComponent && globalTaxEnabled
					? Number(cabsTaxPercentage || 0)
					: undefined,
			componentTaxAppliedOn: cabsTaxAppliedOn,
			rounding: globalRounding,
			givenCurrency: GIVEN_PRICE_CURRENCY,
			exchangeRate: exchangeRate,
		})
	}, [
		totalCabsOnlyPriceInCostingCurrency,
		cabsMarkup,
		globalTaxEnabled,
		globalTaxPercentage,
		taxEditablePerComponent,
		cabsTaxPercentage,
		cabsTaxAppliedOn,
		globalTaxAppliedOn,
		costPriceCurrency,
		globalRounding,
		GIVEN_PRICE_CURRENCY,
		exchangeRate,
	])

	const activitiesResults = useMemo(() => {
		return getComponentPriceCalculationValues({
			base: totalTravelActivitiesOnlyPriceInCostingCurrency,
			markup: Number(activitiesMarkup || 0),
			tax:
				globalTaxEnabled && !taxEditablePerComponent
					? Number(globalTaxPercentage || 0)
					: 0,
			taxIncluded: globalTaxEnabled,
			currency: costPriceCurrency,
			globalTaxAppliedOn: globalTaxAppliedOn || undefined,
			componentTaxPercentage:
				taxEditablePerComponent && globalTaxEnabled
					? Number(activitiesTaxPercentage || 0)
					: undefined,
			componentTaxAppliedOn: activitiesTaxAppliedOn,
			rounding: globalRounding,
			givenCurrency: GIVEN_PRICE_CURRENCY,
			exchangeRate: exchangeRate,
		})
	}, [
		totalTravelActivitiesOnlyPriceInCostingCurrency,
		activitiesMarkup,
		globalTaxEnabled,
		globalTaxPercentage,
		taxEditablePerComponent,
		activitiesTaxPercentage,
		activitiesTaxAppliedOn,
		globalTaxAppliedOn,
		costPriceCurrency,
		globalRounding,
		GIVEN_PRICE_CURRENCY,
		exchangeRate,
	])

	const extrasResults = useMemo(() => {
		return getComponentPriceCalculationValues({
			base: totalExtrasPriceInCostingCurrency,
			markup: Number(extrasMarkup || 0),
			tax:
				globalTaxEnabled && !taxEditablePerComponent
					? Number(globalTaxPercentage || 0)
					: 0,
			taxIncluded: globalTaxEnabled,
			currency: costPriceCurrency,
			globalTaxAppliedOn: globalTaxAppliedOn || undefined,
			componentTaxPercentage:
				taxEditablePerComponent && globalTaxEnabled
					? Number(extrasTaxPercentage || 0)
					: undefined,
			componentTaxAppliedOn: extrasTaxAppliedOn,
			rounding: globalRounding,
			givenCurrency: GIVEN_PRICE_CURRENCY,
			exchangeRate: exchangeRate,
		})
	}, [
		totalExtrasPriceInCostingCurrency,
		extrasMarkup,
		globalTaxEnabled,
		globalTaxPercentage,
		taxEditablePerComponent,
		extrasTaxPercentage,
		extrasTaxAppliedOn,
		globalTaxAppliedOn,
		costPriceCurrency,
		globalRounding,
		GIVEN_PRICE_CURRENCY,
		exchangeRate,
	])

	const flightsResults = useMemo(() => {
		return getFlightComponentPriceCalculationValues({
			costPrice: flightCostPriceInCostingCurrency,
			givenPriceWithTax: flightGivenPriceInCostingCurrency,
			taxPercentage: Number(flightsTaxPercentage || 0),
			taxIncluded: globalTaxEnabled,
			taxAppliedOn: flightsTaxAppliedOn,
			currency: costPriceCurrency,
			givenCurrency: GIVEN_PRICE_CURRENCY,
			exchangeRate: exchangeRate,
			rounding: globalRounding,
		})
	}, [
		flightCostPriceInCostingCurrency,
		flightGivenPriceInCostingCurrency,
		flightsTaxPercentage,
		flightsTaxAppliedOn,
		costPriceCurrency,
		GIVEN_PRICE_CURRENCY,
		exchangeRate,
		globalRounding,
		globalTaxEnabled,
	])

	const subTotalSummary = useMemo(() => {
		const totalUnrounded = [
			hotelResults.unrounded,
			cabsResults.unrounded,
			activitiesResults.unrounded,
			extrasResults.unrounded,
		].reduce(
			(acc, val) => addMoney(acc, val),
			createMoney(0, costPriceCurrency)
		)

		let totalUnroundedInGiven = totalUnrounded

		if (!isCostPriceCurrencySameAsGivenPriceCurrency && exchangeRate) {
			totalUnroundedInGiven = [
				hotelResults.unroundedInGiven,
				cabsResults.unroundedInGiven,
				activitiesResults.unroundedInGiven,
				extrasResults.unroundedInGiven,
			].reduce(
				(acc, val) => addMoney(acc, val),
				createMoney(0, GIVEN_PRICE_CURRENCY)
			)
		}

		const totalTax = [
			hotelResults.tax,
			cabsResults.tax,
			activitiesResults.tax,
			extrasResults.tax,
		].reduce(
			(acc, val) => addMoney(acc, val),
			createMoney(0, costPriceCurrency)
		)

		const totalRounded = [
			hotelResults.rounded,
			cabsResults.rounded,
			activitiesResults.rounded,
			extrasResults.rounded,
		].reduce(
			(acc, val) => addMoney(acc, val),
			createMoney(0, costPriceCurrency)
		)

		let totalRoundedInGiven = totalRounded
		if (!isCostPriceCurrencySameAsGivenPriceCurrency && exchangeRate) {
			totalRoundedInGiven = [
				hotelResults.roundedInGiven,
				cabsResults.roundedInGiven,
				activitiesResults.roundedInGiven,
				extrasResults.roundedInGiven,
			].reduce(
				(acc, val) => addMoney(acc, val),
				createMoney(0, GIVEN_PRICE_CURRENCY)
			)
		}

		const totalMarkup = [
			hotelResults.markup,
			cabsResults.markup,
			activitiesResults.markup,
			extrasResults.markup,
		].reduce(
			(acc, val) => addMoney(acc, val),
			createMoney(0, costPriceCurrency)
		)

		return {
			totalUnrounded,
			totalTax,
			totalRounded,
			totalMarkup,
			totalUnroundedInGiven,
			totalRoundedInGiven,
		}
	}, [
		hotelResults,
		cabsResults,
		activitiesResults,
		extrasResults,
		costPriceCurrency,
		isCostPriceCurrencySameAsGivenPriceCurrency,
		exchangeRate,
		GIVEN_PRICE_CURRENCY,
	])

	const totalSummary = useMemo(() => {
		const totalUnroundedWithFlight = addMoney(
			subTotalSummary.totalUnrounded,
			flightsResults.unrounded
		)

		let totalUnroundedWithFlightInGiven = totalUnroundedWithFlight

		if (!isCostPriceCurrencySameAsGivenPriceCurrency) {
			totalUnroundedWithFlightInGiven = addMoney(
				subTotalSummary.totalUnroundedInGiven,
				flightsResults.unroundedInGiven
			)
		}

		const totalTax = addMoney(subTotalSummary.totalTax, flightsResults.tax)

		const totalMarkup = addMoney(
			subTotalSummary.totalMarkup,
			flightsResults.markup
		)

		const totalRoundedWithFlight = addMoney(
			subTotalSummary.totalRounded,
			flightsResults.rounded
		)

		let totalRoundedWithFlightInGiven = totalRoundedWithFlight

		if (!isCostPriceCurrencySameAsGivenPriceCurrency) {
			totalRoundedWithFlightInGiven = addMoney(
				subTotalSummary.totalRoundedInGiven,
				flightsResults.roundedInGiven
			)
		}

		return {
			totalTax,
			totalMarkup,
			totalUnrounded: totalUnroundedWithFlight,
			totalUnroundedInGiven: totalUnroundedWithFlightInGiven,
			totalRounded: totalRoundedWithFlight,
			totalRoundedInGiven: totalRoundedWithFlightInGiven,
		}
	}, [
		subTotalSummary,
		flightsResults,
		isCostPriceCurrencySameAsGivenPriceCurrency,
	])

	// @dev-note - In the pricing strategy per component per person, user wants to add markup,
	// tax on components but wants the given price rounding based on per-person in the per-person table,
	// we need to scale the unrounded given price by the scaling factor to get the rounded given price according
	// to the per-person rounding so that the total given price in the component table matches
	// the total given price in the per-person table

	const totalGivenPriceInGivenCurrencyWithoutFlightPrice = useMemo(() => {
		return getTotalGivenPriceWithoutFlighPriceInGivenCurrency(
			flightsResults,
			moneyParseByDecimal(Number(givenPrice || 0), GIVEN_PRICE_CURRENCY),
			isCostPriceCurrencySameAsGivenPriceCurrency
		)
	}, [
		flightsResults,
		GIVEN_PRICE_CURRENCY,
		givenPrice,
		isCostPriceCurrencySameAsGivenPriceCurrency,
	])

	const perPersonRoundedGivenPriceScalingFactor = useMemo(() => {
		if (!isPerPersonPricingEnabled || !globalRounding) return undefined

		const scalingFactor = getMoneyRatio(
			totalGivenPriceInGivenCurrencyWithoutFlightPrice,
			subTotalSummary.totalUnroundedInGiven,
			1
		)

		return scalingFactor
	}, [
		totalGivenPriceInGivenCurrencyWithoutFlightPrice,
		isPerPersonPricingEnabled,
		subTotalSummary.totalUnroundedInGiven,
		globalRounding,
	])

	const hotelResultsForPerPersonPricing = useMemo(() => {
		// For multi-option quotes in per-person pricing:
		// Hotels can have different costs per option, so we calculate hotel's scaled price
		// using: hotel_scaled_price = total_given_price - sum_of_non_hotel_components_from_option1
		if (
			optionIndex !== 0 &&
			isPerPersonPricingEnabled &&
			option1GivenPricesPerComponent
		) {
			// Get sub-total given price for this option (calculated from per-person pricing)
			const currentOptionTotalGivenPriceWithoutFlightPrice = Number(
				formatMoneyByDecimal(
					totalGivenPriceInGivenCurrencyWithoutFlightPrice
				) || 0
			)

			// Sum all non-hotel component(without flights) prices from option 1's scaled values
			const nonHotelComponentsTotal = option1GivenPricesPerComponent
				.filter((c) => c.id !== "hotels" && c.id !== "flights")
				.reduce((sum, component) => sum + Number(component.price || 0), 0)

			// Calculate hotel scaled price: total - other components
			const hotelScaledPrice =
				currentOptionTotalGivenPriceWithoutFlightPrice - nonHotelComponentsTotal

			return {
				...hotelResults,
				roundedInGiven: moneyParseByDecimal(
					Math.max(0, hotelScaledPrice),
					GIVEN_PRICE_CURRENCY
				),
			}
		}

		// Option 1: Apply scaling based on perPersonRoundedGivenPriceScalingFactor for 1st option only to get the scaled price based on per person final sub-total given price drived after rounding
		if (!perPersonRoundedGivenPriceScalingFactor) return hotelResults

		const componentPriceRoundedByPerPersonInGivenPriceCurrency =
			multipleMoneyBy(
				hotelResults.unroundedInGiven,
				perPersonRoundedGivenPriceScalingFactor
			)

		return {
			...hotelResults,
			roundedInGiven: roundMoney(
				componentPriceRoundedByPerPersonInGivenPriceCurrency
			),
		}
	}, [
		hotelResults,
		perPersonRoundedGivenPriceScalingFactor,
		optionIndex,
		isPerPersonPricingEnabled,
		option1GivenPricesPerComponent,
		GIVEN_PRICE_CURRENCY,
		totalGivenPriceInGivenCurrencyWithoutFlightPrice,
	])

	const cabsResultsForPerPersonPricing = useMemo(() => {
		// For multi-option quotes, non-hotel components should use Option 1's scaled per-person values
		// to ensure consistency across options since they have identical cost/markup/tax
		if (
			optionIndex !== 0 &&
			isPerPersonPricingEnabled &&
			option1GivenPricesPerComponent
		) {
			const option1CabsComponent = option1GivenPricesPerComponent.find(
				(c) => c.id === "cabs"
			)
			const option1CabsGivenPrice = option1CabsComponent?.price || 0

			return {
				...cabsResults,
				roundedInGiven: moneyParseByDecimal(
					option1CabsGivenPrice,
					GIVEN_PRICE_CURRENCY
				),
			}
		}

		// Option 1: Apply scaling based on perPersonRoundedGivenPriceScalingFactor for 1st option only to get the scaled price based on per person final sub-total given price drived after rounding
		if (!perPersonRoundedGivenPriceScalingFactor) return cabsResults

		const componentPriceRoundedByPerPersonInGivenPriceCurrency =
			multipleMoneyBy(
				cabsResults.unroundedInGiven,
				perPersonRoundedGivenPriceScalingFactor
			)

		return {
			...cabsResults,
			roundedInGiven: roundMoney(
				componentPriceRoundedByPerPersonInGivenPriceCurrency
			),
		}
	}, [
		cabsResults,
		perPersonRoundedGivenPriceScalingFactor,
		optionIndex,
		isPerPersonPricingEnabled,
		option1GivenPricesPerComponent,
		GIVEN_PRICE_CURRENCY,
	])

	const activitiesResultsForPerPersonPricing = useMemo(() => {
		// For multi-option quotes, non-hotel components should use Option 1's scaled per-person values
		// to ensure consistency across options since they have identical cost/markup/tax
		if (
			optionIndex !== 0 &&
			isPerPersonPricingEnabled &&
			option1GivenPricesPerComponent
		) {
			const option1ActivitiesComponent = option1GivenPricesPerComponent.find(
				(c) => c.id === "activities"
			)
			const option1ActivitiesGivenPrice = option1ActivitiesComponent?.price || 0

			return {
				...activitiesResults,
				roundedInGiven: moneyParseByDecimal(
					option1ActivitiesGivenPrice,
					GIVEN_PRICE_CURRENCY
				),
			}
		}

		// Option 1: Apply scaling based on perPersonRoundedGivenPriceScalingFactor for 1st option only to get the scaled price based on per person final sub-total given price drived after rounding

		if (!perPersonRoundedGivenPriceScalingFactor) return activitiesResults

		const componentPriceRoundedByPerPersonInGivenPriceCurrency =
			multipleMoneyBy(
				activitiesResults.unroundedInGiven,
				perPersonRoundedGivenPriceScalingFactor
			)

		return {
			...activitiesResults,
			roundedInGiven: roundMoney(
				componentPriceRoundedByPerPersonInGivenPriceCurrency
			),
		}
	}, [
		activitiesResults,
		perPersonRoundedGivenPriceScalingFactor,
		optionIndex,
		isPerPersonPricingEnabled,
		option1GivenPricesPerComponent,
		GIVEN_PRICE_CURRENCY,
	])

	const extrasResultsForPerPersonPricing = useMemo(() => {
		// For multi-option quotes, non-hotel components should use Option 1's scaled per-person values
		// to ensure consistency across options since they have identical cost/markup/tax
		if (
			optionIndex !== 0 &&
			isPerPersonPricingEnabled &&
			option1GivenPricesPerComponent
		) {
			const option1ExtrasComponent = option1GivenPricesPerComponent.find(
				(c) => c.id === "extras"
			)
			const option1ExtrasGivenPrice = option1ExtrasComponent?.price || 0

			return {
				...extrasResults,
				roundedInGiven: moneyParseByDecimal(
					option1ExtrasGivenPrice,
					GIVEN_PRICE_CURRENCY
				),
			}
		}

		// Option 1: Apply scaling based on perPersonRoundedGivenPriceScalingFactor for 1st option only to get the scaled price based on per person final sub-total given price drived after rounding

		if (!perPersonRoundedGivenPriceScalingFactor) return extrasResults

		const componentPriceRoundedByPerPersonInGivenPriceCurrency =
			multipleMoneyBy(
				extrasResults.unroundedInGiven,
				perPersonRoundedGivenPriceScalingFactor
			)

		return {
			...extrasResults,
			roundedInGiven: roundMoney(
				componentPriceRoundedByPerPersonInGivenPriceCurrency
			),
		}
	}, [
		extrasResults,
		perPersonRoundedGivenPriceScalingFactor,
		optionIndex,
		isPerPersonPricingEnabled,
		option1GivenPricesPerComponent,
		GIVEN_PRICE_CURRENCY,
	])

	const flightsResultForPerPersonPricing = useMemo(() => {
		//flights component should not be scaled for per person pricing strategy,
		// because this component is handled differenly in the quotation page,
		// client usually don't earn propfits on this component that's why they directly
		// add final given price and cost price of this componet and in the pricing strategies
		// there is no featue for adding cutom markup and tax on this component,
		// that's why we are just returing the "flightsResults" directly here.
		return flightsResults
	}, [flightsResults])

	const subTotalSummaryPerPersonPricing = useMemo(() => {
		if (!isPerPersonPricingEnabled) return subTotalSummary

		return {
			...subTotalSummary,
			totalRoundedInGiven: totalGivenPriceInGivenCurrencyWithoutFlightPrice, //it should match the total given price in the per-person table, so that the total given price in the component table matches the total given price in the per-person table
		}
	}, [
		subTotalSummary,
		isPerPersonPricingEnabled,
		totalGivenPriceInGivenCurrencyWithoutFlightPrice,
	])

	const totalSummaryPerPersonPricing = useMemo(() => {
		if (!isPerPersonPricingEnabled) return totalSummary

		let flightPriceInGivenCurrency = flightsResults.rounded

		if (!isCostPriceCurrencySameAsGivenPriceCurrency) {
			flightPriceInGivenCurrency = flightsResults.roundedInGiven
		}

		return {
			...totalSummary,
			totalRoundedInGiven: addMoney(
				subTotalSummaryPerPersonPricing.totalRoundedInGiven,
				flightPriceInGivenCurrency
			),
		}
	}, [
		flightsResults,
		totalSummary,
		isCostPriceCurrencySameAsGivenPriceCurrency,
		isPerPersonPricingEnabled,
		subTotalSummaryPerPersonPricing,
	])

	const perComponentResults = useMemo(() => {
		return {
			hotels: {
				tax: Number(formatMoneyByDecimal(hotelResults.tax) || 0),
				markup: Number(formatMoneyByDecimal(hotelResults.markup) || 0),
				cost_price: Number(
					formatMoneyByDecimal(totalHotelPriceInCostingCurrency) || 0
				),
				given_price: isCostPriceCurrencySameAsGivenPriceCurrency
					? Number(formatMoneyByDecimal(hotelResults.rounded) || 0)
					: Number(formatMoneyByDecimal(hotelResults.roundedInGiven) || 0),
				given_price_unrounded: isCostPriceCurrencySameAsGivenPriceCurrency
					? Number(formatMoneyByDecimal(hotelResults.unrounded) || 0)
					: Number(formatMoneyByDecimal(hotelResults.unroundedInGiven) || 0),
			},
			cabs: {
				tax: Number(formatMoneyByDecimal(cabsResults.tax) || 0),
				markup: Number(formatMoneyByDecimal(cabsResults.markup) || 0),
				cost_price: Number(
					formatMoneyByDecimal(totalCabsOnlyPriceInCostingCurrency) || 0
				),
				given_price: isCostPriceCurrencySameAsGivenPriceCurrency
					? Number(formatMoneyByDecimal(cabsResults.rounded) || 0)
					: Number(formatMoneyByDecimal(cabsResults.roundedInGiven) || 0),
				given_price_unrounded: isCostPriceCurrencySameAsGivenPriceCurrency
					? Number(formatMoneyByDecimal(cabsResults.unrounded) || 0)
					: Number(formatMoneyByDecimal(cabsResults.unroundedInGiven) || 0),
			},
			activities: {
				tax: Number(formatMoneyByDecimal(activitiesResults.tax) || 0),
				markup: Number(formatMoneyByDecimal(activitiesResults.markup) || 0),
				cost_price: Number(
					formatMoneyByDecimal(
						totalTravelActivitiesOnlyPriceInCostingCurrency
					) || 0
				),
				given_price: isCostPriceCurrencySameAsGivenPriceCurrency
					? Number(formatMoneyByDecimal(activitiesResults.rounded) || 0)
					: Number(formatMoneyByDecimal(activitiesResults.roundedInGiven) || 0),
				given_price_unrounded: isCostPriceCurrencySameAsGivenPriceCurrency
					? Number(formatMoneyByDecimal(activitiesResults.unrounded) || 0)
					: Number(
							formatMoneyByDecimal(activitiesResults.unroundedInGiven) || 0
						),
			},
			extras: {
				tax: Number(formatMoneyByDecimal(extrasResults.tax) || 0),
				markup: Number(formatMoneyByDecimal(extrasResults.markup) || 0),
				cost_price: Number(
					formatMoneyByDecimal(totalExtrasPriceInCostingCurrency) || 0
				),
				given_price: isCostPriceCurrencySameAsGivenPriceCurrency
					? Number(formatMoneyByDecimal(extrasResults.rounded) || 0)
					: Number(formatMoneyByDecimal(extrasResults.roundedInGiven) || 0),
				given_price_unrounded: isCostPriceCurrencySameAsGivenPriceCurrency
					? Number(formatMoneyByDecimal(extrasResults.unrounded) || 0)
					: Number(formatMoneyByDecimal(extrasResults.unroundedInGiven) || 0),
			},
			flights: {
				tax: Number(formatMoneyByDecimal(flightsResults.tax) || 0),
				markup: Number(formatMoneyByDecimal(flightsResults.markup) || 0),
				markup_percentage: flightsResults.markupPercentage,
				cost_price: Number(
					formatMoneyByDecimal(flightCostPriceInCostingCurrency) || 0
				),
				given_price: isCostPriceCurrencySameAsGivenPriceCurrency
					? Number(formatMoneyByDecimal(flightsResults.rounded) || 0)
					: Number(formatMoneyByDecimal(flightsResults.roundedInGiven) || 0),
				given_price_unrounded: isCostPriceCurrencySameAsGivenPriceCurrency
					? Number(formatMoneyByDecimal(flightsResults.unrounded) || 0)
					: Number(formatMoneyByDecimal(flightsResults.unroundedInGiven) || 0),
			},
		}
	}, [
		hotelResults,
		cabsResults,
		activitiesResults,
		extrasResults,
		flightsResults,
		flightCostPriceInCostingCurrency,
		totalHotelPriceInCostingCurrency,
		totalCabsOnlyPriceInCostingCurrency,
		totalTravelActivitiesOnlyPriceInCostingCurrency,
		totalExtrasPriceInCostingCurrency,
		isCostPriceCurrencySameAsGivenPriceCurrency,
	])

	const perComponentResultsPerPersonPricing = useMemo(() => {
		return {
			hotels: {
				...perComponentResults.hotels,
				given_price: Number(
					formatMoneyByDecimal(
						hotelResultsForPerPersonPricing.roundedInGiven
					) || 0
				),
			},
			cabs: {
				...perComponentResults.cabs,
				given_price: Number(
					formatMoneyByDecimal(cabsResultsForPerPersonPricing.roundedInGiven) ||
						0
				),
			},
			activities: {
				...perComponentResults.activities,
				given_price: Number(
					formatMoneyByDecimal(
						activitiesResultsForPerPersonPricing.roundedInGiven
					) || 0
				),
			},
			extras: {
				...perComponentResults.extras,
				given_price: Number(
					formatMoneyByDecimal(
						extrasResultsForPerPersonPricing.roundedInGiven
					) || 0
				),
			},
			flights: {
				...perComponentResults.flights,
				given_price: Number(
					formatMoneyByDecimal(
						flightsResultForPerPersonPricing.roundedInGiven
					) || 0
				),
			},
		}
	}, [
		hotelResultsForPerPersonPricing,
		cabsResultsForPerPersonPricing,
		activitiesResultsForPerPersonPricing,
		extrasResultsForPerPersonPricing,
		flightsResultForPerPersonPricing,
		perComponentResults,
	])

	const totalPerComponentCostPriceComponentsTotal = useMemo(() => {
		return totalCostPriceWithoutFlightPrice
	}, [totalCostPriceWithoutFlightPrice])

	const totalTax = useMemo(() => {
		return Number(formatMoneyByDecimal(subTotalSummary.totalTax) || 0)
	}, [subTotalSummary.totalTax])

	const totalMarkup = useMemo(() => {
		return Number(formatMoneyByDecimal(subTotalSummary.totalMarkup) || 0)
	}, [subTotalSummary.totalMarkup])

	const totalMarkupPercentage = useMemo(() => {
		return totalPerComponentCostPriceComponentsTotal > 0
			? normalizeNumber(
					(totalMarkup / totalPerComponentCostPriceComponentsTotal) * 100,
					6
				)
			: 0
	}, [totalMarkup, totalPerComponentCostPriceComponentsTotal])

	const subTotaUnroundedGivenPriceInGivenCurrency = useMemo(() => {
		let subTotalUnroundedInGivenCurrency = subTotalSummary.totalUnrounded

		if (!isCostPriceCurrencySameAsGivenPriceCurrency) {
			subTotalUnroundedInGivenCurrency = subTotalSummary.totalUnroundedInGiven
		}

		return Number(formatMoneyByDecimal(subTotalUnroundedInGivenCurrency) || 0)
	}, [
		subTotalSummary.totalUnrounded,
		subTotalSummary.totalUnroundedInGiven,
		isCostPriceCurrencySameAsGivenPriceCurrency,
	])

	const totalUnroundedGivenPriceInGivenCurrency = useMemo(() => {
		let totalUnroundedInGivenCurrency = totalSummary.totalUnrounded

		if (!isCostPriceCurrencySameAsGivenPriceCurrency) {
			totalUnroundedInGivenCurrency = totalSummary.totalUnroundedInGiven
		}

		return Number(formatMoneyByDecimal(totalUnroundedInGivenCurrency) || 0)
	}, [
		totalSummary.totalUnrounded,
		totalSummary.totalUnroundedInGiven,
		isCostPriceCurrencySameAsGivenPriceCurrency,
	])

	const totalGivenPriceInGivenCurrencyWithFlightPrice = useMemo(() => {
		let flightPriceInGivenCurrency = flightsResults.rounded

		if (!isCostPriceCurrencySameAsGivenPriceCurrency) {
			flightPriceInGivenCurrency = flightsResults.roundedInGiven
		}

		const totalGivenPriceWithFlightPriceInGivenPriceCurrency = addMoney(
			subTotalSummary.totalRoundedInGiven,
			flightPriceInGivenCurrency
		)
		return Number(
			formatMoneyByDecimal(
				totalGivenPriceWithFlightPriceInGivenPriceCurrency
			) || 0
		)
	}, [
		subTotalSummary.totalRoundedInGiven,
		isCostPriceCurrencySameAsGivenPriceCurrency,
		flightsResults,
	])

	return (
		<>
			<Stack gap="6">
				<Heading fontSize="md" color="accent">
					Set Markup, Tax and Rounding (Per-Component)
				</Heading>
				<Table bordered hover responsive fixedLayout={fixedLayoutTable}>
					<thead>
						<tr>
							<TableHeaderDataCell
								style={{
									width: "200px",
								}}
							>
								Component
							</TableHeaderDataCell>
							<TableHeaderDataCell bgColor="inset">
								<Stack gap="1" textAlign={"right"}>
									<Text>Cost Price</Text>
									<Text>({costPriceCurrency})</Text>
								</Stack>
							</TableHeaderDataCell>
							<TableHeaderDataCell
								bgColor="inset"
								style={{
									width: "180px",
								}}
							>
								<Stack gap="1" textAlign={"center"}>
									<Text>Markup</Text>
									<SelectInputField
										name={`options[0].margins.use_percentage`}
										size="sm"
										disabled={disableMarkupEditingForOption}
										onChange={(e) => {
											const value = e.currentTarget.value

											if (value === "0") {
												changeGlobalMarkupUsePercentage(0)
											} else {
												changeGlobalMarkupUsePercentage(1)
											}
										}}
										{...props}
									>
										<option value={0} label="Amount" title="Amount">
											Amount
										</option>
										<option value={1} label="Percentage" title="Percentage">
											Percentage
										</option>
									</SelectInputField>
								</Stack>
							</TableHeaderDataCell>
							<TableHeaderDataCell
								bgColor="inset"
								style={{
									width: "180px",
								}}
							>
								<Stack gap="1" textAlign={"center"}>
									<Text>Tax Applied On</Text>

									{globalTaxEnabled ? (
										<SelectInputField
											name={`options[0].taxes.total_applied_on`}
											size="sm"
											disabled={disableTaxEditingForOption}
											onChange={(e) => {
												const value = e.currentTarget.value
												changeTotalAppliedOn(value)

												if (!value) {
													changeGlobalTaxEditingTotal(0)
												}
											}}
											{...props}
										>
											<option
												value="cost_plus_markup"
												label="Cost + Markup"
												title="Cost + Markup"
											>
												Cost + Markup
											</option>
											<option
												value="only_markup"
												label="Only Markup"
												title="Only Markup"
											>
												Only Markup
											</option>
											<option value="" label="Custom">
												Custom
											</option>
										</SelectInputField>
									) : null}
								</Stack>
							</TableHeaderDataCell>
							<TableHeaderDataCell bgColor="inset">
								<Component initialState={false}>
									{({ state: taxDisabled, setState: setTaxDisabled }) => (
										<GetFieldValue<0 | 1> name={`options[0].gst_included`}>
											{({
												value: globalTaxEnabled,
												onChange: onGlobalTaxEnabledChange,
											}) => (
												<Stack gap="1" alignItems="center">
													<Inline
														gap="1"
														alignItems="center"
														justifyContent="center"
													>
														{taxDisabled ? null : (
															<SwitchInputField
																name={`options[0].gst_included`}
																disabled={disableTaxEditingForOption}
																{...props}
															/>
														)}
														<GetFieldValue<number | "">
															name={`options[0].taxes.total_percentage`}
														>
															{({ onChange: changeTaxPercentage }) => (
																<InlineSelectTaxTypeInputField
																	name="tax_name"
																	onChangeDefaultValue={(value) => {
																		changeTaxPercentage(value)
																	}}
																	onDisabledTaxValueSelection={(disabled) => {
																		setTaxDisabled(disabled)
																		if (disabled) {
																			onGlobalTaxEnabledChange(0)
																		}
																	}}
																	disabled={disableTaxEditingForOption}
																	{...props}
																/>
															)}
														</GetFieldValue>
														{!taxDisabled
															? isTruthy(globalTaxEnabled)
																? null
																: "(exc)"
															: null}
													</Inline>
													<Box>
														{isTruthy(globalTaxEnabled) ? (
															<ComponentPricingTaxGlobalSettingsInputField
																optionIndex={0}
																taxEditablePerComponent={
																	taxEditablePerComponent ? 1 : 0
																}
																changeEditingTotal={changeGlobalTaxEditingTotal}
																disabled={
																	disableTaxEditingForOption ||
																	!globalTaxAppliedOn
																}
																{...props}
															/>
														) : null}
													</Box>
												</Stack>
											)}
										</GetFieldValue>
									)}
								</Component>
							</TableHeaderDataCell>

							<TableHeaderDataCell bgColor="inset">
								<Stack gap="1" textAlign={"right"}>
									<Text>Total Tax</Text>
									<Text>({costPriceCurrency})</Text>
								</Stack>
							</TableHeaderDataCell>

							<TableHeaderDataCell bgColor="inset">
								<Stack gap="1" textAlign={"right"}>
									<Text> Total</Text>
									<Text>({costPriceCurrency})</Text>
								</Stack>
							</TableHeaderDataCell>
							{GIVEN_PRICE_CURRENCY &&
							GIVEN_PRICE_CURRENCY !== costPriceCurrency ? (
								<TableHeaderDataCell bgColor="inset" textAlign="right">
									<Stack gap="1" textAlign={"right"}>
										<Text>Total</Text>
										<Text>({GIVEN_PRICE_CURRENCY})</Text>
									</Stack>
								</TableHeaderDataCell>
							) : null}

							<TableHeaderDataCell borderLeftWidth="1" bgColor="primary">
								<Inline
									gap="1"
									alignItems={"center"}
									justifyContent={"end"}
									flexWrap="wrap"
								>
									<Text>Round</Text>

									{isPerPersonPricingEnabled ? null : (
										<Box
											paddingX="2"
											bgColor="default"
											rounded="default"
											borderWidth="1"
										>
											{!disableRoundingEditingForOption ? (
												<InlineSelectInputField
													name={`options[0].margins.rounding_point`}
													style={{ width: 80 }}
													disabled={disableRoundingEditingForOption}
													{...props}
												>
													{ROUNDING_OPTIONS.map((o, i) => (
														<option value={o || ""} key={i}>
															{o || "--"}
														</option>
													))}
												</InlineSelectInputField>
											) : (
												<Box>{globalRounding}</Box>
											)}
										</Box>
									)}
								</Inline>
							</TableHeaderDataCell>
						</tr>
					</thead>

					<tbody>
						<ComponentMarkupTableRow
							componentKey={"hotels"}
							componentLabel={"Hotels"}
							optionIndex={optionIndex}
							base={totalHotelPriceInCostingCurrency}
							markupUsePercentage={markupUsePercentage}
							globalTaxEnabled={globalTaxEnabled}
							globalTaxPercentage={globalTaxPercentage}
							costPriceCurrency={costPriceCurrency}
							givenPriceCurrency={GIVEN_PRICE_CURRENCY}
							globalTaxAppliedOn={globalTaxAppliedOn}
							taxEditablePerComponent={taxEditablePerComponent}
							changeEditingTotal={changeGlobalTaxEditingTotal}
							values={
								isPerPersonPricingEnabled
									? hotelResultsForPerPersonPricing
									: hotelResults
							}
							componentTaxPercentage={hotelTaxPercentage || undefined}
							componentMarkup={hotelMarkup || undefined}
							componentMarkupPercentage={hotelMarkupPercentage || undefined}
							changeMarkup={changeHotelMarkup}
							changeMarkupPercentage={changeHotelMarkupPercentage}
							changeComponentTaxPercentage={changeHotelTaxPercentage}
							disableTaxEditing={disableTaxEditingForOption}
							disableMarkupEditing={readOnlyMode}
						/>
						<ComponentMarkupTableRow
							componentKey={"cabs"}
							componentLabel={"Transports"}
							optionIndex={0}
							base={totalCabsOnlyPriceInCostingCurrency}
							markupUsePercentage={markupUsePercentage}
							globalTaxEnabled={globalTaxEnabled}
							globalTaxPercentage={globalTaxPercentage}
							costPriceCurrency={costPriceCurrency}
							givenPriceCurrency={GIVEN_PRICE_CURRENCY}
							globalTaxAppliedOn={globalTaxAppliedOn}
							taxEditablePerComponent={taxEditablePerComponent}
							changeEditingTotal={changeGlobalTaxEditingTotal}
							values={
								isPerPersonPricingEnabled
									? cabsResultsForPerPersonPricing
									: cabsResults
							}
							componentTaxPercentage={cabsTaxPercentage || undefined}
							componentMarkup={cabsMarkup || undefined}
							componentMarkupPercentage={cabsMarkupPercentage || undefined}
							changeMarkup={changeCabsMarkup}
							changeMarkupPercentage={changeCabsMarkupPercentage}
							changeComponentTaxPercentage={changeCabsTaxPercentage}
							disableMarkupEditing={disableMarkupEditingForOption}
							disableTaxEditing={disableTaxEditingForOption}
						/>
						<ComponentMarkupTableRow
							componentKey={"activities"}
							componentLabel={"Activities"}
							optionIndex={0}
							base={totalTravelActivitiesOnlyPriceInCostingCurrency}
							markupUsePercentage={markupUsePercentage}
							globalTaxEnabled={globalTaxEnabled}
							globalTaxPercentage={globalTaxPercentage}
							costPriceCurrency={costPriceCurrency}
							givenPriceCurrency={GIVEN_PRICE_CURRENCY}
							globalTaxAppliedOn={globalTaxAppliedOn}
							taxEditablePerComponent={taxEditablePerComponent}
							changeEditingTotal={changeGlobalTaxEditingTotal}
							values={
								isPerPersonPricingEnabled
									? activitiesResultsForPerPersonPricing
									: activitiesResults
							}
							componentTaxPercentage={activitiesTaxPercentage || undefined}
							componentMarkup={activitiesMarkup || undefined}
							componentMarkupPercentage={
								activitiesMarkupPercentage || undefined
							}
							changeMarkup={changeActivitiesMarkup}
							changeMarkupPercentage={changeActivitiesMarkupPercentage}
							changeComponentTaxPercentage={changeActivitiesTaxPercentage}
							disableMarkupEditing={disableMarkupEditingForOption}
							disableTaxEditing={disableTaxEditingForOption}
						/>
						<ComponentMarkupTableRow
							componentKey={"extras"}
							componentLabel={"Transport/Other Extras"}
							optionIndex={0}
							base={totalExtrasPriceInCostingCurrency}
							markupUsePercentage={markupUsePercentage}
							globalTaxEnabled={globalTaxEnabled}
							globalTaxPercentage={globalTaxPercentage}
							costPriceCurrency={costPriceCurrency}
							givenPriceCurrency={GIVEN_PRICE_CURRENCY}
							globalTaxAppliedOn={globalTaxAppliedOn}
							taxEditablePerComponent={taxEditablePerComponent}
							changeEditingTotal={changeGlobalTaxEditingTotal}
							values={
								isPerPersonPricingEnabled
									? extrasResultsForPerPersonPricing
									: extrasResults
							}
							componentTaxPercentage={extrasTaxPercentage || undefined}
							componentMarkup={extrasMarkup || undefined}
							componentMarkupPercentage={extrasMarkupPercentage || undefined}
							changeMarkup={changeExtrasMarkup}
							changeMarkupPercentage={changeExtrasMarkupPercentage}
							changeComponentTaxPercentage={changeExtrasTaxPercentage}
							disableMarkupEditing={disableMarkupEditingForOption}
							disableTaxEditing={disableTaxEditingForOption}
						/>
					</tbody>
					<tfoot>
						<ComponentMarkupTaxPricingSummaryRow
							label={
								flightCostPriceInCostingCurrency &&
								flightCostPriceInCostingCurrency.amount > 0
									? "Sub-Total"
									: "Total"
							}
							totalCostPrice={moneyParseByDecimal(
								totalCostPriceWithoutFlightPrice,
								costPriceCurrency
							)}
							givenPriceCurrency={GIVEN_PRICE_CURRENCY}
							costPriceCurrency={costPriceCurrency}
							summary={
								isPerPersonPricingEnabled
									? subTotalSummaryPerPersonPricing
									: subTotalSummary
							}
						/>
						{flightCostPriceInCostingCurrency &&
						flightCostPriceInCostingCurrency.amount > 0 ? (
							<>
								<ComponentMarkupTableRow
									componentKey={"flights"}
									componentLabel={"Flights"}
									optionIndex={0}
									globalTaxEnabled={globalTaxEnabled}
									base={flightCostPriceInCostingCurrency}
									costPriceCurrency={costPriceCurrency}
									givenPriceCurrency={GIVEN_PRICE_CURRENCY}
									values={flightsResults}
									componentTaxPercentage={flightsTaxPercentage || undefined}
									changeComponentTaxPercentage={changeFlightsTaxPercentage}
									disableMarkupEditing={true}
									readOnlyMarkup
									disableTaxEditing={disableTaxEditingForOption}
									componentMarkup={Number(
										formatMoneyByDecimal(flightsResults.markup) || 0
									)}
								/>
								<ComponentMarkupTaxPricingSummaryRow
									label="Total"
									totalCostPrice={moneyParseByDecimal(
										totalCostPriceWithFlightPrice,
										costPriceCurrency
									)}
									givenPriceCurrency={GIVEN_PRICE_CURRENCY}
									costPriceCurrency={costPriceCurrency}
									summary={
										isPerPersonPricingEnabled
											? totalSummaryPerPersonPricing
											: totalSummary
									}
								/>
							</>
						) : null}
					</tfoot>
				</Table>

				{isPerPersonPricingEnabled ? (
					<TotalTravelComponentMarkupTaxPerPersonPreview
						optionIndex={optionIndex}
						exchangeRate={exchangeRate}
						totalMarkup={subTotalSummary.totalMarkup}
						totalTax={subTotalSummary.totalTax}
						totalUnroundedInCostingCurrency={subTotalSummary.totalUnrounded}
						totalUnroundedInGivenCurrency={
							subTotalSummary.totalUnroundedInGiven
						}
						totalCostPriceWithoutFlightPrice={moneyParseByDecimal(
							Number(totalCostPriceWithoutFlightPrice || 0),
							costPriceCurrency
						)}
						costPriceCurrency={costPriceCurrency}
						givenPriceCurrency={givenPriceCurrency}
						flightCostPriceInCostingCurrency={flightCostPriceInCostingCurrency}
						flightGivenPriceInCostingCurrency={
							flightGivenPriceInCostingCurrency
						}
						flightsResults={flightsResults}
						globalTaxEnabled={globalTaxEnabled}
						globalTaxAppliedOn={
							globalTaxAppliedOn
								? globalTaxAppliedOn === "cost_plus_markup"
									? "Cost + Markup"
									: "Only Markup"
								: undefined
						}
						globalTaxPercentage={
							!taxEditablePerComponent ? globalTaxPercentage : undefined
						}
						disableRounding={disableRoundingEditingForOption}
						perPersonAllocationNotPossible={perPersonAllocationNotPossible}
						fixedLayoutTable={fixedLayoutTable}
						{...props}
					/>
				) : null}
			</Stack>

			<WarnForSignificantRoundingIncrement
				totalGivenPriceBeforeRounding={subTotalSummary.totalUnroundedInGiven}
				totalGivenPriceAfterRounding={subTotalSummary.totalRoundedInGiven}
				roundingPoint={globalRounding}
			/>

			<SyncAllComponentMarkupTotalsAndPerComponent
				optionIndex={optionIndex}
				perComponentResults={
					isPerPersonPricingEnabled
						? perComponentResultsPerPersonPricing
						: perComponentResults
				}
				totalTaxValue={totalTax}
				totalMarkupValue={totalMarkup}
				totalMarkupPercentage={totalMarkupPercentage}
				subTotalUnroundedGivenPriceInGivenCurrency={
					subTotaUnroundedGivenPriceInGivenCurrency
				}
				totalUnroundedGivenPriceInGivenCurrency={
					totalUnroundedGivenPriceInGivenCurrency
				}
				totalGivenPriceInGivenCurrency={
					totalGivenPriceInGivenCurrencyWithFlightPrice
				}
				totalPerComponentCostPriceComponentsTotal={
					totalPerComponentCostPriceComponentsTotal
				}
				isPerPersonPricingEnabled={isPerPersonPricingEnabled}
			/>
		</>
	)
}

function ComponentPricingTaxGlobalSettingsInputField({
	optionIndex,
	taxEditablePerComponent,
	changeEditingTotal,
	disabled,
	...props
}: {
	optionIndex: number
	taxEditablePerComponent: 0 | 1
	changeEditingTotal: (value: 0 | 1) => void
	disabled?: boolean
}) {
	return (
		<Inline gap="1" alignItems="center">
			{!isTruthy(taxEditablePerComponent) ? (
				<PercentageInputField
					name={`options[${optionIndex}].taxes.total_percentage`}
					size="sm"
					style={{ width: "80px" }}
					disabled={disabled}
					{...props}
				/>
			) : (
				`---`
			)}
			{isTruthy(taxEditablePerComponent) ? (
				<Button
					size="sm"
					level="tertiary"
					onClick={() => {
						changeEditingTotal(isTruthy(taxEditablePerComponent) ? 1 : 0)
					}}
					disabled={disabled}
					{...props}
				>
					<Icons.Pencil />
				</Button>
			) : null}
		</Inline>
	)
}

function SyncAllComponentMarkupTotalsAndPerComponent({
	optionIndex,
	perComponentResults,
	totalTaxValue,
	totalMarkupValue,
	totalMarkupPercentage,
	subTotalUnroundedGivenPriceInGivenCurrency,
	totalUnroundedGivenPriceInGivenCurrency,
	totalGivenPriceInGivenCurrency,
	totalPerComponentCostPriceComponentsTotal,
	isPerPersonPricingEnabled,
	perPersonAllocationNotPossible,
}: {
	optionIndex: number
	perComponentResults: Record<
		TComponentKey,
		{
			tax: number
			markup: number
			markup_percentage?: number
			cost_price: number
			given_price: number
			given_price_unrounded: number
		}
	>
	totalTaxValue?: number
	totalMarkupValue?: number
	totalMarkupPercentage?: number
	subTotalUnroundedGivenPriceInGivenCurrency?: number
	totalUnroundedGivenPriceInGivenCurrency?: number
	totalGivenPriceInGivenCurrency?: number
	totalPerComponentCostPriceComponentsTotal?: number
	isPerPersonPricingEnabled?: boolean
	perPersonAllocationNotPossible?: boolean
}) {
	const form = useForm()

	useEffect(() => {
		const h = setTimeout(() => {
			form.batch(() => {
				const state = form.getState()

				// --- PER-COMPONENT: cost_prices_per_component ---

				const newCostPricesPerComponent = Object.entries(
					perComponentResults
				).map(([key, val]) => ({
					id: key,
					price: val.cost_price,
				}))

				const currentCostPricesPerComponent =
					state.values.options?.[optionIndex]?.cost_prices_per_component || []
				if (
					!isDeepEqual(currentCostPricesPerComponent, newCostPricesPerComponent)
				) {
					form.change(
						`options[${optionIndex}].cost_prices_per_component`,
						newCostPricesPerComponent
					)
				}

				// --- PER-COMPONENT: given_prices_per_component ---

				const newGivenPricesPerComponent = Object.entries(
					perComponentResults
				).map(([key, val]) => ({
					id: key,
					price: val.given_price,
				}))

				const currentGivenPricesPerComponent =
					state.values.options?.[optionIndex]?.given_prices_per_component || []
				if (
					!isDeepEqual(
						currentGivenPricesPerComponent,
						newGivenPricesPerComponent
					)
				) {
					form.change(
						`options[${optionIndex}].given_prices_per_component`,
						newGivenPricesPerComponent
					)
				}

				// --- TAX: per-component ---
				for (const key of Object.keys(perComponentResults)) {
					const newTax = perComponentResults[key as TComponentKey]?.tax
					const currentTax =
						state.values.options?.[optionIndex]?.taxes
							?.per_component_allocation?.[key]
					if (typeof newTax === "number" && currentTax !== newTax) {
						form.change(
							`options[${optionIndex}].taxes.per_component_allocation.${key}`,
							newTax
						)
					}
				}

				// --- TAX: total ---
				const currentTaxTotal =
					state.values.options?.[optionIndex]?.taxes?.total
				if (
					typeof totalTaxValue === "number" &&
					!isNaN(totalTaxValue) &&
					currentTaxTotal !== totalTaxValue
				) {
					form.change(`options[${optionIndex}].taxes.total`, totalTaxValue)
				}

				// --- MARKUP: per-component allocation (override flights only) ---
				const currentPerComponentAllocation =
					state.values.options?.[optionIndex]?.margins
						?.per_component_allocation || {}

				const newPerComponentAllocation = {
					...currentPerComponentAllocation,
					flights: perComponentResults.flights?.markup || 0,
					flights_percentage:
						perComponentResults.flights?.markup_percentage || 0,
				}

				// We will only overide and set the state if the flights markup, percentage changed otherwise for other components expect flights there is no need to change form state
				// as each component table row has the capability to set the calculated absoulute markup,
				// percentage to the form state inside the `options.margins.per_component_allocation.`${componentkey}`
				if (
					currentPerComponentAllocation.flights !==
						newPerComponentAllocation.flights ||
					currentPerComponentAllocation.flights_percentage !==
						newPerComponentAllocation.flights_percentage
				) {
					form.change(
						`options[${optionIndex}].margins.per_component_allocation`,
						newPerComponentAllocation
					)
				}

				// --- UNROUNDED GIVEN PRICES: total, subtotal, per_component_allocation, per_person_allocation ---

				const currentUnroundedGivenPrices =
					state.values.options?.[optionIndex]?.unrounded_given_prices || {}

				const newUnroundedGivenPrices = {
					...currentUnroundedGivenPrices,
					total: totalUnroundedGivenPriceInGivenCurrency,
					subtotal: subTotalUnroundedGivenPriceInGivenCurrency,
					per_component_allocation: Object.entries(perComponentResults).reduce(
						(acc, [key, val]) => ({
							...acc,
							[key]: val.given_price_unrounded,
						}),
						{} as Record<TComponentKey, number>
					),
				}

				if (
					!isDeepEqual(currentUnroundedGivenPrices, newUnroundedGivenPrices)
				) {
					form.change(
						`options[${optionIndex}].unrounded_given_prices`,
						newUnroundedGivenPrices
					)
				}

				// --- MARKUP: total ---
				const currentMarkupTotal =
					state.values.options?.[optionIndex]?.margins?.total
				if (
					typeof totalMarkupValue === "number" &&
					!isNaN(totalMarkupValue) &&
					currentMarkupTotal !== totalMarkupValue
				) {
					form.change(`options[${optionIndex}].margins.total`, totalMarkupValue)
				}

				// --- GIVEN PRICE: total in Given Currency ---
				const currentGivenPriceTotalInGivenCurrency =
					state.values.options?.[optionIndex]?.given_price
				if (
					typeof totalGivenPriceInGivenCurrency === "number" &&
					!isNaN(totalGivenPriceInGivenCurrency) &&
					currentGivenPriceTotalInGivenCurrency !==
						totalGivenPriceInGivenCurrency
				) {
					if (!isPerPersonPricingEnabled || perPersonAllocationNotPossible) {
						form.change(
							`options[${optionIndex}].given_price`,
							totalGivenPriceInGivenCurrency
						)
					}
				}

				// --- per_component_cost_price_components - TOTAl ---
				const currentPerComponentCostPriceComponentsTotal =
					state.values.options?.[optionIndex]
						?.per_component_cost_price_components?.total

				if (
					typeof totalPerComponentCostPriceComponentsTotal === "number" &&
					!isNaN(totalPerComponentCostPriceComponentsTotal) &&
					currentPerComponentCostPriceComponentsTotal !==
						totalPerComponentCostPriceComponentsTotal
				) {
					form.change(
						`options[${optionIndex}].per_component_cost_price_components.total`,
						totalPerComponentCostPriceComponentsTotal
					)
				}
			})
		}, 100)
		return () => clearTimeout(h)
	}, [
		optionIndex,
		perComponentResults,
		totalTaxValue,
		totalMarkupValue,
		totalGivenPriceInGivenCurrency,
		subTotalUnroundedGivenPriceInGivenCurrency,
		totalPerComponentCostPriceComponentsTotal,
		totalUnroundedGivenPriceInGivenCurrency,
		perPersonAllocationNotPossible,
		isPerPersonPricingEnabled,
		totalMarkupPercentage,
		form,
	])

	return null
}

export function ComponentMarkupTaxPerPersonPricingBifurcationWithRounding({
	optionIndex,
	costPriceCurrency,
	quoteComponentPrices,
	children,
	totalExtrasPrice,
	hasCabs,
	perPersonAllocationResult,
}: {
	optionIndex: number
	costPriceCurrency: string
	quoteComponentPrices: IQuoteComponentsCostPricesDistributed
	children: INewQuoteState["children"]
	totalExtrasPrice: TMoney[]
	hasCabs: boolean
	perPersonAllocationResult: TPerPersonAllocationResult
}) {
	const {
		paxAllocation,
		paxAllocationError,
		paxAllocationWarnings,
		priceMismatchBetweenPerPersonAndTotal,
		totalCostPriceExcludingFlights,
		totalPriceUsingAllocation,
		perPersonAllocationNotPossible,
	} = perPersonAllocationResult

	return (
		<Stack gap="6">
			<Grid gap="6">
				{paxAllocation?.length ? (
					<Col sm={12} lg={4} xl={3}>
						<Stack gap="4">
							<Heading fontSize="md" color="accent">
								Per-Person Settings
							</Heading>
							<PerPersonInclusionConfiguration
								children={children}
								paxAllocation={paxAllocation}
								optionIndex={optionIndex}
								totalExtrasPrice={totalExtrasPrice}
								hasCabs={hasCabs}
							/>
						</Stack>
					</Col>
				) : null}
				<Col>
					<Stack gap="4">
						{!paxAllocation ? (
							<PerPersonAllocationNotPossibleError
								paxAllocationError={paxAllocationError}
							/>
						) : (
							<>
								<PerPersonAllocationWarnings
									paxAllocationWarnings={paxAllocationWarnings}
								/>
								<PerPersonCostPriceSegregationTable
									costPriceCurrency={costPriceCurrency}
									paxAllocation={paxAllocation}
								/>
							</>
						)}
						{priceMismatchBetweenPerPersonAndTotal ? (
							<PerPersonAllocationPriceMismatchError
								costPriceCurrency={costPriceCurrency}
								totalCostPriceExcludingFlights={totalCostPriceExcludingFlights}
								totalPriceUsingAllocation={totalPriceUsingAllocation}
							/>
						) : null}
					</Stack>
				</Col>
			</Grid>

			<ComponentMarkupTaxPricingBifurcation
				optionIndex={optionIndex}
				isPerPersonPricingEnabled={true}
				perPersonAllocationNotPossible={perPersonAllocationNotPossible}
				quoteComponentPrices={quoteComponentPrices}
			/>
		</Stack>
	)
}

function TotalTravelComponentMarkupTaxPerPersonPreview({
	optionIndex,
	totalMarkup,
	totalTax,
	totalUnroundedInCostingCurrency,
	totalUnroundedInGivenCurrency,
	flightCostPriceInCostingCurrency,
	totalCostPriceWithoutFlightPrice,
	costPriceCurrency,
	givenPriceCurrency,
	flightsResults,
	globalTaxAppliedOn,
	globalTaxEnabled,
	globalTaxPercentage,
	disableRounding,
	perPersonAllocationNotPossible,
	fixedLayoutTable,
	...props
}: {
	optionIndex: number
	exchangeRate: number
	totalMarkup: TMoney
	totalTax: TMoney
	totalUnroundedInCostingCurrency: TMoney
	totalUnroundedInGivenCurrency: TMoney
	totalCostPriceWithoutFlightPrice: TMoney
	flightGivenPriceInCostingCurrency: TMoney
	flightCostPriceInCostingCurrency: TMoney
	costPriceCurrency: string
	givenPriceCurrency: string
	globalTaxEnabled: boolean
	flightsResults: ReturnType<typeof getFlightComponentPriceCalculationValues>
	globalTaxAppliedOn?: string
	globalTaxPercentage?: number
	disableRounding?: boolean
	perPersonAllocationNotPossible?: boolean
	fixedLayoutTable?: boolean
	readOnly?: boolean
}) {
	const { value: taxName } =
		useFieldValue<INewQuoteState["tax_name"]>("tax_name")

	const { value: globalRounding } = useFieldValue<number | undefined>(
		`options[0].margins.rounding_point`
	)

	const { value: perPersonCostPriceAllocation } = useFieldValue<
		ITripQuote["cost_prices_per_person"] | undefined
	>(`options[${optionIndex}].cost_prices_per_person`)

	const { value: costPriceGivenPriceCurrencyPairRatio } =
		useCurrencyPairRatioFieldValue(
			"currency_pairs_obj",
			costPriceCurrency,
			givenPriceCurrency
		)

	const totalBaseAllPax = useMemo(() => {
		return totalCostPriceWithoutFlightPrice
	}, [totalCostPriceWithoutFlightPrice])

	const roundingPoint = globalRounding
	const costPriceToGivenPriceExchaneRate =
		costPriceGivenPriceCurrencyPairRatio || 1
	const isCostPriceCurrencySameAsGivenPriceCurrency =
		costPriceCurrency === givenPriceCurrency

	const perPersonRows = useMemo(() => {
		if (
			!perPersonCostPriceAllocation ||
			perPersonCostPriceAllocation.length === 0
		)
			return []

		const groupBaseTotals = perPersonCostPriceAllocation.map((pax) =>
			multipleMoneyBy(
				moneyParseByDecimal(Number(pax.price || 0), costPriceCurrency),
				pax.pax_count
			)
		)
		const groupShares = groupBaseTotals.map((groupBaseTotal) =>
			getMoneyRatio(groupBaseTotal, totalBaseAllPax, 0)
		)
		const groupMarkups = groupShares.map((share) =>
			multipleMoneyBy(totalMarkup, share)
		)
		const groupTaxes = groupShares.map((share) =>
			multipleMoneyBy(totalTax, share)
		)

		return perPersonCostPriceAllocation.map((pax, i) => {
			const perPersonBasePrice = moneyParseByDecimal(
				Number(pax.price || 0),
				costPriceCurrency
			)
			const markupAllocations = allocateMoneyTo(groupMarkups[i], pax.pax_count)
			const taxAllocations = allocateMoneyTo(groupTaxes[i], pax.pax_count)
			const perPersonMarkup = markupAllocations[0]
			const perPersonTax = taxAllocations[0]
			const unroundedMoneyPerPerson = addMoney(
				perPersonBasePrice,
				perPersonMarkup,
				perPersonTax
			)
			let roundedMoneyPerPerson = unroundedMoneyPerPerson
			if (roundingPoint) {
				roundedMoneyPerPerson = roundMoneyToPoint(
					unroundedMoneyPerPerson,
					roundingPoint
				)
			}

			let unroundedPerPersonInGiven = unroundedMoneyPerPerson
			let roundedPerPersonInGiven = roundedMoneyPerPerson
			if (
				givenPriceCurrency &&
				costPriceToGivenPriceExchaneRate &&
				givenPriceCurrency !== costPriceCurrency
			) {
				unroundedPerPersonInGiven = convertMoneyToCurrency(
					unroundedMoneyPerPerson,
					givenPriceCurrency,
					costPriceToGivenPriceExchaneRate
				)
				if (roundingPoint) {
					roundedPerPersonInGiven = roundMoneyToPoint(
						unroundedPerPersonInGiven,
						roundingPoint
					)
				} else {
					roundedPerPersonInGiven = unroundedPerPersonInGiven
				}
			}

			return {
				...pax,
				base: perPersonBasePrice,
				markup: perPersonMarkup,
				tax: perPersonTax,
				markupAllocations,
				taxAllocations,
				unrounded: unroundedMoneyPerPerson,
				rounded: roundedMoneyPerPerson,
				unroundedInGiven: unroundedPerPersonInGiven,
				roundedInGiven: roundedPerPersonInGiven,
			}
		})
	}, [
		perPersonCostPriceAllocation,
		totalBaseAllPax,
		roundingPoint,
		costPriceToGivenPriceExchaneRate,
		givenPriceCurrency,
		costPriceCurrency,
		totalMarkup,
		totalTax,
	])

	const subTotalSummary = useMemo(() => {
		let totalRoundedInGivenCurrency = createMoney(
			0,
			givenPriceCurrency || costPriceCurrency
		)

		if (globalRounding) {
			perPersonRows.forEach((p) => {
				for (let i = 0; i < p.pax_count; i++) {
					totalRoundedInGivenCurrency = addMoney(
						totalRoundedInGivenCurrency,
						p.roundedInGiven
					)
				}
			})
		} else {
			totalRoundedInGivenCurrency = totalUnroundedInGivenCurrency
		}

		return {
			totalBaseAllPax,
			totalMarkup,
			totalTax,
			totalUnroundedInCostingCurrency,
			totalUnroundedInGivenCurrency,
			totalRoundedInGivenCurrency,
		}
	}, [
		perPersonRows,
		costPriceCurrency,
		totalBaseAllPax,
		givenPriceCurrency,
		totalMarkup,
		totalTax,
		totalUnroundedInCostingCurrency,
		totalUnroundedInGivenCurrency,
		globalRounding,
	])

	const totalSummary = useMemo(() => {
		let flightPriceUnroundedInGivenCurrency = flightsResults.unrounded
		let flightPriceRoundedInGivenCurrency = flightsResults.rounded

		if (!isCostPriceCurrencySameAsGivenPriceCurrency) {
			flightPriceRoundedInGivenCurrency = flightsResults.roundedInGiven
			flightPriceUnroundedInGivenCurrency = flightsResults.unroundedInGiven
		}

		return {
			...subTotalSummary,
			totalCostPrice: addMoney(
				subTotalSummary.totalBaseAllPax,
				flightCostPriceInCostingCurrency
			),
			totalMarkup: addMoney(subTotalSummary.totalMarkup, flightsResults.markup),
			totalTax: addMoney(subTotalSummary.totalTax, flightsResults.tax),
			totalUnroundedInCostingCurrency: addMoney(
				subTotalSummary.totalUnroundedInCostingCurrency,
				flightsResults.unrounded
			),
			totalUnroundedInGivenCurrency: addMoney(
				subTotalSummary.totalUnroundedInGivenCurrency,
				flightPriceUnroundedInGivenCurrency
			),
			totalRoundedInGivenCurrency: addMoney(
				subTotalSummary.totalRoundedInGivenCurrency,
				flightPriceRoundedInGivenCurrency
			),
		}
	}, [
		subTotalSummary,
		flightsResults,
		isCostPriceCurrencySameAsGivenPriceCurrency,
		flightCostPriceInCostingCurrency,
	])

	const totalPerPersonCosPriceComponentsPriceInCostingCurrency = useMemo(() => {
		return Number(formatMoneyByDecimal(subTotalSummary.totalBaseAllPax) || 0)
	}, [subTotalSummary])

	const totalGivenPricePerPersonWithFlightInGivenCurrency = useMemo(() => {
		let flightPriceInGivenCurrency = flightsResults.rounded

		if (!isCostPriceCurrencySameAsGivenPriceCurrency) {
			flightPriceInGivenCurrency = flightsResults.roundedInGiven
		}

		const totalGivenPriceWithFlightPriceUsingPerPersonAllocation = addMoney(
			subTotalSummary.totalRoundedInGivenCurrency,
			flightPriceInGivenCurrency
		)
		return Number(
			formatMoneyByDecimal(
				totalGivenPriceWithFlightPriceUsingPerPersonAllocation
			) || 0
		)
	}, [
		subTotalSummary.totalRoundedInGivenCurrency,
		isCostPriceCurrencySameAsGivenPriceCurrency,
		flightsResults,
	])

	const perPersonDistributedGivenPriceAllocation = useMemo(() => {
		if (!perPersonCostPriceAllocation || !perPersonCostPriceAllocation.length)
			return undefined

		return perPersonCostPriceAllocation.map((pax) => {
			const perPersonAllocatedPrices = (perPersonRows || []).find(
				(p) => p.id === pax.id
			)

			if (!perPersonAllocatedPrices) {
				return {
					...pax,
					price: "0",
					price_unrounded: "0",
					currency: givenPriceCurrency,
				}
			}

			const paxGivenPrice = formatMoneyByDecimal(
				perPersonAllocatedPrices.roundedInGiven
			)
			const paxUnroundedGivenPrice = formatMoneyByDecimal(
				perPersonAllocatedPrices.unroundedInGiven
			)

			return {
				...pax,
				price: paxGivenPrice,
				price_unrounded: paxUnroundedGivenPrice,
				currency: givenPriceCurrency,
			}
		})
	}, [perPersonCostPriceAllocation, perPersonRows, givenPriceCurrency])

	const perPersonDistributedMarkupAllocation = useMemo(() => {
		if (!perPersonRows || perPersonRows.length === 0) return undefined

		const allocation: Record<string, number> = {}

		const totalBaseAllPax = Number(
			formatMoneyByDecimal(subTotalSummary.totalBaseAllPax) || 0
		)
		const subTotalMarkup = Number(
			formatMoneyByDecimal(subTotalSummary.totalMarkup) || 0
		)

		//Calculated Using "SubTotalCostPrice - (Total Base All Pax) and Sub Total Makrup"
		const perPersonMarkupPercentage =
			totalBaseAllPax > 0
				? normalizeNumber((subTotalMarkup / totalBaseAllPax) * 100, 6)
				: 0

		perPersonRows.forEach((p, i) => {
			const keyBase = p.id

			// Absolute margin (amount)
			allocation[`${keyBase}`] = Number(formatMoneyByDecimal(p.markup) || 0)

			// Percentage margin
			allocation[`${keyBase}_percentage`] = perPersonMarkupPercentage
		})

		return allocation
	}, [
		perPersonRows,
		subTotalSummary.totalBaseAllPax,
		subTotalSummary.totalMarkup,
	])

	return (
		<>
			{perPersonAllocationNotPossible ? null : (
				<Stack gap="4">
					<Heading fontSize="md" color="accent">
						Per Person Sharing with Rounding
					</Heading>
					<Table bordered hover responsive fixedLayout={fixedLayoutTable}>
						<thead>
							<tr>
								<TableHeaderDataCell
									style={{
										width: "200px",
									}}
								></TableHeaderDataCell>
								<TableHeaderDataCell bgColor="inset" textAlign="right">
									<Stack gap="1">
										<Text>Cost Price</Text>
										<Text>({costPriceCurrency})</Text>
									</Stack>
								</TableHeaderDataCell>
								<TableHeaderDataCell
									bgColor="inset"
									textAlign={"center"}
									style={{
										width: "180px",
									}}
								>
									<Text>Markup</Text>
								</TableHeaderDataCell>
								<TableHeaderDataCell
									bgColor="inset"
									textAlign="center"
									style={{
										width: "180px",
									}}
								>
									<Stack gap="1">
										<Text>Tax Applied On</Text>
									</Stack>
								</TableHeaderDataCell>

								<TableHeaderDataCell bgColor="inset" textAlign="center">
									<Inline gap="1" alignItems="center" justifyContent="center">
										<Text>{taxName}</Text>
										<Text>{globalTaxEnabled ? null : "(exc)"}</Text>
									</Inline>
								</TableHeaderDataCell>
								<TableHeaderDataCell bgColor="inset" textAlign="right">
									<Stack gap="1">
										<Text>Total Tax</Text>
										<Text>({costPriceCurrency})</Text>
									</Stack>
								</TableHeaderDataCell>
								<TableHeaderDataCell bgColor="inset" textAlign="right">
									<Stack gap="1">
										<Text>Total</Text>
										<Text>({costPriceCurrency})</Text>
									</Stack>
								</TableHeaderDataCell>
								{givenPriceCurrency &&
								givenPriceCurrency !== costPriceCurrency ? (
									<TableHeaderDataCell bgColor={"inset"} textAlign="right">
										<Stack gap="1">
											<Text>Total</Text>
											<Text>({givenPriceCurrency})</Text>
										</Stack>
									</TableHeaderDataCell>
								) : null}
								<TableHeaderDataCell
									textAlign="right"
									borderLeftWidth="1"
									bgColor="primary"
								>
									<Inline
										gap="1"
										justifyContent="end"
										alignItems="center"
										flexWrap="wrap"
									>
										<Text>Round</Text>

										<Box
											paddingX="2"
											bgColor="default"
											rounded="default"
											borderWidth="1"
										>
											{!disableRounding ? (
												<InlineSelectInputField
													name={`options[0].margins.rounding_point`}
													style={{ maxWidth: "90px" }}
													disabled={disableRounding}
													{...props}
												>
													{ROUNDING_OPTIONS.map((o, i) => (
														<option value={o || ""} key={i}>
															{o || "--"}
														</option>
													))}
												</InlineSelectInputField>
											) : (
												<Box>{globalRounding || "N/A"}</Box>
											)}
										</Box>
									</Inline>
								</TableHeaderDataCell>
							</tr>
						</thead>
						<tbody>
							{perPersonRows.map((p, i) => {
								if (p.base.amount === 0) {
									return null
								}
								return (
									<tr key={i}>
										<TableDataCell bgColor="inset">
											<Stack gap="1">
												<Text>{p.per_pax_label}</Text>
												<Text fontSize="sm">x {p.total_pax_label}</Text>
											</Stack>
										</TableDataCell>
										<TableDataCell textAlign="right">
											<Stack gap="1">
												<Money money={p.base} />
												<Text fontSize="sm">x{p.pax_count}</Text>
											</Stack>
										</TableDataCell>
										<TableDataCell textAlign="center">
											<Money money={p.markup} />
										</TableDataCell>
										<TableDataCell textAlign="center">
											{globalTaxEnabled ? globalTaxAppliedOn || "-" : "-"}
										</TableDataCell>
										<TableDataCell textAlign="center">
											{globalTaxEnabled
												? globalTaxPercentage
													? `${globalTaxPercentage} %`
													: "-"
												: "-"}
										</TableDataCell>
										<TableDataCell textAlign="right">
											<Money money={p.tax} />
										</TableDataCell>
										<TableDataCell textAlign="right">
											<Money money={p.unrounded} />
										</TableDataCell>
										{!isCostPriceCurrencySameAsGivenPriceCurrency ? (
											<TableDataCell textAlign="right">
												<Money money={p.unroundedInGiven} />
											</TableDataCell>
										) : null}
										<TableDataCell textAlign="right">
											<Stack gap="1">
												<Money money={p.roundedInGiven} />
												<Text fontSize="sm">x{p.pax_count}</Text>
											</Stack>
										</TableDataCell>
									</tr>
								)
							})}
						</tbody>

						<tfoot>
							<tr
								style={{
									fontStyle: "italic",
								}}
							>
								<TableFooterDataCell bgColor="inset" fontWeight="semibold">
									Sub-Total
								</TableFooterDataCell>
								<TableFooterDataCell textAlign="right" fontWeight="semibold">
									<Money money={subTotalSummary.totalBaseAllPax} />
								</TableFooterDataCell>
								<TableFooterDataCell textAlign="center" fontWeight="semibold">
									<Money money={subTotalSummary.totalMarkup} />
								</TableFooterDataCell>
								<TableFooterDataCell
									textAlign="center"
									fontWeight="semibold"
								></TableFooterDataCell>
								<TableFooterDataCell
									textAlign="center"
									fontWeight="semibold"
								></TableFooterDataCell>
								<TableFooterDataCell textAlign="right" fontWeight="semibold">
									<Money money={subTotalSummary.totalTax} />
								</TableFooterDataCell>
								<TableFooterDataCell textAlign="right" fontWeight="semibold">
									<Money
										money={subTotalSummary.totalUnroundedInCostingCurrency}
									/>
								</TableFooterDataCell>
								{!isCostPriceCurrencySameAsGivenPriceCurrency ? (
									<TableFooterDataCell textAlign="right" fontWeight="semibold">
										<Money
											money={subTotalSummary.totalUnroundedInGivenCurrency}
										/>
									</TableFooterDataCell>
								) : null}
								<TableFooterDataCell textAlign="right" fontWeight="semibold">
									<Money
										money={subTotalSummary.totalRoundedInGivenCurrency}
										showCurrency
									/>
								</TableFooterDataCell>
							</tr>
							{flightCostPriceInCostingCurrency.amount > 0 && flightsResults ? (
								<tr>
									<TableFooterDataCell bgColor="inset" fontWeight="semibold">
										Flights
									</TableFooterDataCell>
									<TableFooterDataCell textAlign="right" fontWeight="semibold">
										<Money money={flightCostPriceInCostingCurrency} />
									</TableFooterDataCell>
									<TableFooterDataCell textAlign="center" fontWeight="semibold">
										<Money money={flightsResults.markup} />
									</TableFooterDataCell>
									<TableFooterDataCell textAlign="center" fontWeight="semibold">
										{globalTaxEnabled
											? flightsResults.taxAppliedOn === "cost_plus_markup"
												? "Cost + Markup"
												: "Only Markup"
											: "-"}
									</TableFooterDataCell>
									<TableFooterDataCell textAlign="center" fontWeight="semibold">
										{globalTaxEnabled
											? flightsResults.taxPercentage
												? `${flightsResults.taxPercentage} %`
												: "-"
											: "-"}
									</TableFooterDataCell>
									<TableFooterDataCell textAlign="right" fontWeight="semibold">
										<Money money={flightsResults.tax} />
									</TableFooterDataCell>
									<TableFooterDataCell textAlign="right" fontWeight="semibold">
										<Money money={flightsResults.unrounded} />
									</TableFooterDataCell>
									{!isCostPriceCurrencySameAsGivenPriceCurrency ? (
										<TableFooterDataCell
											textAlign="right"
											fontWeight="semibold"
										>
											<Money money={flightsResults.unroundedInGiven} />
										</TableFooterDataCell>
									) : null}
									<TableFooterDataCell textAlign="right" fontWeight="semibold">
										<Money money={flightsResults.roundedInGiven} showCurrency />
									</TableFooterDataCell>
								</tr>
							) : null}
							<tr>
								<TableFooterDataCell bgColor="warning" fontWeight="semibold">
									Total
								</TableFooterDataCell>
								<TableFooterDataCell
									bgColor={"warning"}
									textAlign="right"
									fontWeight="semibold"
								>
									<Money money={totalSummary.totalCostPrice} />
								</TableFooterDataCell>
								<TableFooterDataCell
									bgColor={"warning"}
									textAlign="center"
									fontWeight="semibold"
								>
									<Money money={totalSummary.totalMarkup} />
								</TableFooterDataCell>
								<TableFooterDataCell
									bgColor={"warning"}
									textAlign="center"
									fontWeight="semibold"
								>
									{globalTaxEnabled ? globalTaxAppliedOn || "-" : "-"}
								</TableFooterDataCell>
								<TableFooterDataCell
									bgColor={"warning"}
									textAlign="center"
									fontWeight="semibold"
								>
									{globalTaxEnabled
										? globalTaxPercentage
											? `${globalTaxPercentage} %`
											: "-"
										: "-"}
								</TableFooterDataCell>
								<TableFooterDataCell
									bgColor={"warning"}
									textAlign="right"
									fontWeight="semibold"
								>
									<Money money={totalSummary.totalTax} />
								</TableFooterDataCell>
								<TableFooterDataCell
									bgColor={"warning"}
									textAlign="right"
									fontWeight="semibold"
								>
									<Money money={totalSummary.totalUnroundedInCostingCurrency} />
								</TableFooterDataCell>
								{!isCostPriceCurrencySameAsGivenPriceCurrency ? (
									<TableFooterDataCell
										bgColor={"warning"}
										textAlign="right"
										fontWeight="semibold"
									>
										<Money money={totalSummary.totalUnroundedInGivenCurrency} />
									</TableFooterDataCell>
								) : null}
								<TableFooterDataCell
									bgColor={"warning"}
									textAlign="right"
									fontWeight="semibold"
								>
									<Money
										money={totalSummary.totalRoundedInGivenCurrency}
										showCurrency
									/>
								</TableFooterDataCell>
							</tr>
						</tfoot>
					</Table>
				</Stack>
			)}

			<SyncPerPersonPerComponentPricingWithTotal
				optionIndex={optionIndex}
				costPriceCurrency={costPriceCurrency}
				givenPriceCurrency={givenPriceCurrency}
				totalGivenPricePerPersonWithFlightInGivenCurrency={
					totalGivenPricePerPersonWithFlightInGivenCurrency
				}
				totalPerPersonCosPriceComponentsPriceInCostingCurrency={
					totalPerPersonCosPriceComponentsPriceInCostingCurrency
				}
				perPersonCostPriceAllocation={perPersonCostPriceAllocation}
				perPersonDistributedGivenPriceAllocation={
					perPersonDistributedGivenPriceAllocation
				}
				perPersonDistributedMarkupAllocation={
					perPersonDistributedMarkupAllocation
				}
			/>
		</>
	)
}

function SyncPerPersonPerComponentPricingWithTotal({
	optionIndex,
	costPriceCurrency,
	givenPriceCurrency,
	totalGivenPricePerPersonWithFlightInGivenCurrency,
	totalPerPersonCosPriceComponentsPriceInCostingCurrency,
	perPersonCostPriceAllocation,
	perPersonDistributedMarkupAllocation,
	perPersonDistributedGivenPriceAllocation,
}: {
	optionIndex: number
	costPriceCurrency: string
	givenPriceCurrency: string
	totalGivenPricePerPersonWithFlightInGivenCurrency: number
	totalPerPersonCosPriceComponentsPriceInCostingCurrency: number
	perPersonCostPriceAllocation?: ITripQuote["cost_prices_per_person"]
	perPersonDistributedMarkupAllocation?: TPerPersonMarkupAllocation
	perPersonDistributedGivenPriceAllocation?: Array<
		TPricePerPersonAllocation & {
			price_unrounded: string
		}
	>
}) {
	const form = useForm()

	useEffect(() => {
		const h = setTimeout(() => {
			form.batch(() => {
				const state = form.getState()

				if (!perPersonCostPriceAllocation?.length) {
					form.change(
						`options[${optionIndex}].given_prices_per_person`,
						undefined
					)

					form.change(
						`options[${optionIndex}].unrounded_given_prices.per_person_allocation`,
						undefined
					)
					return
				}

				const currentGivenPricesPerPerson =
					state.values.options?.[optionIndex]?.given_prices_per_person

				if (
					!isDeepEqual(
						currentGivenPricesPerPerson,
						perPersonDistributedGivenPriceAllocation
					)
				) {
					form.change(
						`options[${optionIndex}].given_prices_per_person`,
						perPersonDistributedGivenPriceAllocation
					)
				}

				// -- PER PERSON COST PRICE COMPONENTS TOTAL in COSTING CURRENCY ---

				const currentPerPersonCostPriceComponents =
					state.values.options?.[optionIndex]?.per_person_cost_price_components

				const newPerPersonCostPriceComponents = {
					...currentPerPersonCostPriceComponents,
					total: totalPerPersonCosPriceComponentsPriceInCostingCurrency,
				}

				if (
					!isDeepEqual(
						currentPerPersonCostPriceComponents,
						newPerPersonCostPriceComponents
					)
				) {
					form.change(
						`options[${optionIndex}].per_person_cost_price_components`,
						newPerPersonCostPriceComponents
					)
				}

				// -- PER PERSON MARGIN ALLOCATION ---

				const currentPerPersonMarkupAllocation =
					state.values.options?.[optionIndex]?.margins?.per_person_allocation

				if (
					!isDeepEqual(
						currentPerPersonMarkupAllocation,
						perPersonDistributedMarkupAllocation
					)
				) {
					form.change(
						`options[${optionIndex}].margins.per_person_allocation`,
						perPersonDistributedMarkupAllocation
					)
				}

				// --- UNROUNDED GIVEN PRICES: per_person_allocation ---

				const currentPerPersonAllocation =
					state.values.options?.[optionIndex]?.unrounded_given_prices
						?.per_person_allocation

				const newPerPersonAllocation =
					perPersonDistributedGivenPriceAllocation?.reduce(
						(acc, person) => ({
							...acc,
							[person.id]: Number(person.price_unrounded || 0),
						}),
						{} as Record<string, number>
					)

				if (!isDeepEqual(currentPerPersonAllocation, newPerPersonAllocation)) {
					form.change(
						`options[${optionIndex}].unrounded_given_prices.per_person_allocation`,
						newPerPersonAllocation
					)
				}

				// --- GIVEN PRICE: total in Given Currency ---

				const currentGivenPricePerPersonWithFlightPriceInGivenCurrency =
					state.values.options?.[optionIndex]?.given_price

				if (
					typeof totalGivenPricePerPersonWithFlightInGivenCurrency ===
						"number" &&
					!isNaN(totalGivenPricePerPersonWithFlightInGivenCurrency) &&
					currentGivenPricePerPersonWithFlightPriceInGivenCurrency !==
						totalGivenPricePerPersonWithFlightInGivenCurrency
				) {
					form.change(
						`options[${optionIndex}].given_price`,
						totalGivenPricePerPersonWithFlightInGivenCurrency
					)
				}
			})
		}, 100)
		return () => clearTimeout(h)
	}, [
		optionIndex,
		costPriceCurrency,
		givenPriceCurrency,
		perPersonCostPriceAllocation,
		perPersonDistributedGivenPriceAllocation,
		perPersonDistributedMarkupAllocation,
		totalGivenPricePerPersonWithFlightInGivenCurrency,
		totalPerPersonCosPriceComponentsPriceInCostingCurrency,
		form,
	])
	return null
}

function WarnForSignificantRoundingIncrement({
	totalGivenPriceBeforeRounding,
	totalGivenPriceAfterRounding,
	roundingPoint,
}: {
	totalGivenPriceBeforeRounding: TMoney
	totalGivenPriceAfterRounding: TMoney
	roundingPoint?: number
}) {
	const percentageIncrement =
		((totalGivenPriceAfterRounding.amount -
			totalGivenPriceBeforeRounding.amount) *
			100) /
		(totalGivenPriceBeforeRounding.amount || 1)

	if (percentageIncrement < 1) {
		return null
	}

	return (
		<Alert status="warning">
			<Stack gap="2">
				<Text fontSize="md" fontWeight="semibold">
					Possible High Rounding ({Number(percentageIncrement).toFixed(2)}%
					increased)
				</Text>
				<Text>
					The rounding point ({roundingPoint}) has increased the price by{" "}
					<b>
						{Number(percentageIncrement).toFixed(2)}% from{" "}
						<Money money={totalGivenPriceBeforeRounding} showCurrency /> to{" "}
						<Money money={totalGivenPriceAfterRounding} showCurrency />
					</b>
					.
				</Text>
				<Text>Please reconfirm or reduce the rounding if incorrect.</Text>
			</Stack>
		</Alert>
	)
}
